import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/ru';
import times from 'lodash/times';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import Tab from '@material-ui/core/Tab';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Snackbar from '@material-ui/core/Snackbar';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { withStyles } from '@material-ui/core/styles';

import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import {
  MdOutlineAssignment,
  MdOutlineFileDownload,
  MdOutlineFileUpload,
  MdOutlineHelpOutline,
} from 'react-icons/md';

import api, { API_URL } from './../lib/api';
import { formatBookingErrorForUser } from './../lib/bookingErrors';
import { formatUserLabel, normalizeListResponse } from './../lib/sectionClientsAggregate';
import { TIMETABLE_CLIENT_HINT } from './../lib/sectionsGlossaryCopy';
import { orgMembersToTrainerOptions } from './../lib/orgStaff';
import {
  buildClientsUserMap,
  buildComplexClientOptions,
  collectPaidUserIds,
} from './../lib/complexClients';
import { mapOrgClientsToPickerOptions, mergePickerOptions, organizationIdFrom, clientRefFromPickerValue, hasPickerClient, sectionClientWriteFields } from './../lib/orgClientsPicker';
import ComplexClientPicker from './sections/ComplexClientPicker';
import {
  ATTENDANCE_ATTENDED,
  ATTENDANCE_NO_SHOW,
  ATTENDANCE_UNKNOWN,
  attendanceStatusLabel,
  isOccurrenceRosterResponseStale,
  normalizeAttendanceStatus,
  rosterPersonLabel,
} from './../lib/occurrenceRoster';
import {
  buildSectionOccurrenceMap,
  flattenOccurrenceToCells as flattenOccurrenceToCellsLib,
  getOccurrenceIntervalBounds,
  normalizeOccurrenceEvent as normalizeOccurrenceEventLib,
  parseOccurrenceMoment as parseOccurrenceMomentLib,
  resolveSectionDragMode,
  buildColumnRowspans,
  buildDayMergeKeys,
  intervalFromClientYInSpan,
  resolveMergedBlockDragMode,
  clampResizeDropInterval,
  mergedBlockResizePreview,
  sectionDragCursorForMode,
  getIntervalBookingMergeId,
  SECTION_GROUP_SLOT_PALETTE,
  toneForSectionGroupId,
} from './../lib/timetableSectionOverlay';
import { workingHoursTypeEnum, workingHourPartTypeEnum, sportsEnum, sportFlagsToKeys } from './../enums';
import { getSchedule, getTipData, intervalSlotTimeRange } from './timetable.lib';
import { sportFlagsToKeysArray, sportKeysToFlagsArray } from './../lib/sportUtils';
import BookingDialog from './BookingDialog';
import { ADMIN_PRIMARY } from '../theme';

const today = moment().format('YYYY-MM-DD');

/** Единый язык занятых слотов (аренда + секции) — solid chip, как в референсе. */
const TIMETABLE_CHIP = {
  free: { bg: '#ECFDF5', fg: '#64748B' },
  unpaid: { bg: '#F59E0B', fg: '#ffffff' },
  paid: { bg: '#EF4444', fg: '#ffffff' },
  admin: { bg: '#64748B', fg: '#ffffff' },
  section: { bg: ADMIN_PRIMARY, fg: '#ffffff' },
  personal: { bg: '#2563EB', fg: '#ffffff' },
  past: { bg: '#E2E8F0', fg: '#475569' },
  closed: { bg: '#F1F5F9', fg: '#94A3B8' },
};
const TIMETABLE_GRID_LINE = '#E2E8F0';
const TIMETABLE_SELECT = 'rgba(14, 147, 95, 0.22)';
const TIMETABLE_SELECT_BORDER = ADMIN_PRIMARY;

const sortWorkingHourPartValue = {
  [workingHourPartTypeEnum.left]: 0,
  [workingHourPartTypeEnum.middle]: 1,
  [workingHourPartTypeEnum.right]: 2,
};

export class Timetable extends PureComponent {
  constructor(props) {
    super(props);
    this.selectedRange = null; // Выбранный диапазон для передачи в диалог
    /** Инкремент при открытии/закрытии карточки занятия: отсекает поздние ответы roster. */
    this._occurrenceRosterEpoch = 0;
  }

  static propTypes = {
    currentWorkingHoursTypes: PropTypes.string.isRequired,
    sports: PropTypes.object.isRequired,
    playgroundId: PropTypes.number.isRequired,
    workingHoursTypes: PropTypes.array.isRequired,
    classes: PropTypes.object,
    changeCurrentWorkingHoursType: PropTypes.func.isRequired,
    sport: PropTypes.array.isRequired,
    addWorkingHoursType: PropTypes.func.isRequired,
    workingHoursSports: PropTypes.object,
    onWorkingHoursSportsChange: PropTypes.func.isRequired,
    submit: PropTypes.func.isRequired,
    initialDate: PropTypes.string, // Начальная дата для выбора (формат YYYY-MM-DD)
    /** ID комплекса: для фильтров секций и оверлея SectionOccurrence (BE-12/BE-14) */
    sportComplexId: PropTypes.number,
    /** Предвыбор фильтра секции из query (?sectionId=) */
    initialSectionFilterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }

  static defaultProps = {
    classes: {},
    workingHoursSports: {},
    initialDate: null,
    sportComplexId: null,
    initialSectionFilterId: '',
  };

  state = {
    id: this.props.playgroundId,
    selectedDate: this.props.initialDate ? moment(this.props.initialDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'), // Выбранный день (для календаря)
    currentMonth: this.props.initialDate ? moment(this.props.initialDate).startOf('month') : moment().startOf('month'), // Текущий месяц для календаря
    // Начало отображаемой недели (понедельник)
    weekStart: this.props.initialDate
      ? moment(this.props.initialDate, 'YYYY-MM-DD').startOf('isoWeek')
      : moment().startOf('isoWeek'),
    isSnackOpen: false,
    snackMessage: '', // маленькое окошко с уведомлениями для информации из API ("Бронирование удалено")
    // Состояние для выделения диапазона
    selectionStart: null, // { interval, part, day } - начало выделения
    selectionEnd: null,   // { interval, part, day } - конец выделения
    isSelecting: false,   // флаг активного выделения
    /** Режим таймлайна: вся неделя или один выбранный день */
    timelineViewMode: 'week',
    /** Якорь popover-календаря в шапке периода */
    calendarAnchorEl: null,
    /** Якорь popover со справкой по цветам и жестам сетки */
    legendHelpAnchorEl: null,
    sectionsCatalog: [],
    groupsCatalog: [],
    /** '' = все секции на площадке; id = фильтр */
    sectionFilterId: this.props.initialSectionFilterId
      ? String(this.props.initialSectionFilterId)
      : '',
    groupFilterId: '',
    sectionOccurrenceMap: {},
    sectionOccurrencesLoading: false,
    sectionEventDialogOpen: false,
    sectionEventDialogData: null,
    sectionEventRescheduleDate: '',
    sectionEventRescheduleTime: '',
    sectionEventRescheduleEndDate: '',
    sectionEventRescheduleEndTime: '',
    sectionEventRescheduleSaving: false,
    sectionEventRescheduleError: '',
    occurrenceRoster: [],
    occurrenceRosterLoading: false,
    occurrenceRosterError: '',
    occurrenceRosterAddUserId: '',
    occurrenceRosterSaving: false,
    complexClientOptions: [],
    complexClientsLoading: false,
    organizationId: null,
    /** DnD: move | resize-start | resize-end */
    sectionDrag: null, // { event, mode, durationMin, originDay, originInterval, fixedStartAt, fixedEndAt }
    sectionDropTarget: null, // { day, interval }
    sectionDragMoved: false,
    trainerMap: {},
    trainersError: '',
    sectionsCatalogError: '',
    groupsCatalogError: '',
    /** P1: меню действий на свободном слоте */
    slotActionChooserOpen: false,
    slotActionContext: null, // { intervalData, day, part, interval }
    createPersonalDialogOpen: false,
    createPersonalSaving: false,
    createPersonalError: '',
    createPersonalForm: {
      sectionId: '',
      userId: '',
      trainerId: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
      part: null,
    },
    createGroupDialogOpen: false,
    createGroupSaving: false,
    createGroupError: '',
    createGroupGroups: [],
    createGroupGroupsLoading: false,
    createGroupForm: {
      sectionId: '',
      groupId: '',
      trainerId: '',
      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',
    },
  };

  componentDidMount() {
    // Загружаем данные для отображаемой недели
    this.loadWeekData(this.state.weekStart, this.props.currentWorkingHoursTypes);
    // Загружаем данные месяца для календаря (чтобы отображать точки бронирований)
    this.loadMonthData(this.state.currentMonth, this.props.currentWorkingHoursTypes);
    if (this.props.sportComplexId) {
      this.loadSectionsCatalog();
    }
    this.loadTrainerDirectory();

    // Обработчик для отмены выделения при клике вне ячеек
    document.addEventListener('mouseup', this.handleDocumentMouseUp);
    document.addEventListener('mousemove', this.handleDocumentMouseMove);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentDidUpdate(prevProps) {
    // Если изменился initialDate, обновляем выбранную дату и неделю
    if (this.props.initialDate && this.props.initialDate !== prevProps.initialDate) {
      const dateMoment = moment(this.props.initialDate, 'YYYY-MM-DD', true);
      if (dateMoment.isValid()) {
        const newDate = dateMoment.format('YYYY-MM-DD');
        const newMonth = dateMoment.clone().startOf('month');
        const newWeekStart = dateMoment.clone().startOf('isoWeek');
        this.setState({
          selectedDate: newDate,
          currentMonth: newMonth,
          weekStart: newWeekStart,
        });
        this.loadWeekData(newWeekStart, this.props.currentWorkingHoursTypes);
        if (!this.state.currentMonth.isSame(newMonth)) {
          this.loadMonthData(newMonth, this.props.currentWorkingHoursTypes);
        }
      }
    }

    // Если изменился тип расписания, перезагружаем данные
    if (prevProps.currentWorkingHoursTypes !== this.props.currentWorkingHoursTypes) {
      this.loadWeekData(this.state.weekStart, this.props.currentWorkingHoursTypes);
      this.loadMonthData(this.state.currentMonth, this.props.currentWorkingHoursTypes);
    }

    if (prevProps.sportComplexId !== this.props.sportComplexId) {
      this.setState({
        sectionFilterId: '',
        groupFilterId: '',
        groupsCatalog: [],
        sectionOccurrenceMap: {},
        sectionsCatalog: [],
        sectionsCatalogError: '',
        groupsCatalogError: '',
        organizationId: null,
        complexClientOptions: [],
        trainerMap: {},
        trainersError: '',
      });
      if (this.props.sportComplexId) {
        this.loadSectionsCatalog();
        this.loadTrainerDirectory();
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mouseup', this.handleDocumentMouseUp);
    document.removeEventListener('mousemove', this.handleDocumentMouseMove);
    document.removeEventListener('keydown', this.handleKeyDown);
    this.clearPendingCellAction();
    this.detachSheetWrapWheel();
  }

  detachSheetWrapWheel = () => {
    if (this.sheetWrapEl) {
      this.sheetWrapEl.removeEventListener('wheel', this.handleSheetPeriodWheel);
      this.sheetWrapEl = null;
    }
  };

  setSheetWrapRef = (el) => {
    if (this.sheetWrapEl === el) return;
    this.detachSheetWrapWheel();
    this.sheetWrapEl = el;
    if (el) {
      el.addEventListener('wheel', this.handleSheetPeriodWheel, { passive: false });
    }
  };

  /** Горизонтальный скролл/свайп: смена недели или дня (стрелки остаются). */
  canNavigatePeriodByHorizontal = (deltaX) => {
    const el = this.sheetWrapEl;
    if (!el) return true;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 2) return true;
    const atStart = el.scrollLeft <= 1;
    const atEnd = el.scrollLeft >= maxScroll - 1;
    // deltaX > 0 — жест «вперёд» (к следующему периоду), нужен правый край
    if (deltaX > 0) return atEnd;
    if (deltaX < 0) return atStart;
    return false;
  };

  tryNavigatePeriodByGesture = (deltaX) => {
    if (!deltaX || this.state.sectionDrag || this.state.isSelecting) return false;
    if (!this.canNavigatePeriodByHorizontal(deltaX)) return false;
    const now = Date.now();
    if (now < (this._periodNavLockUntil || 0)) return false;
    this._periodNavLockUntil = now + 480;
    this._periodNavWheelAcc = 0;
    // Свайп пальцем влево / скролл трекпада вперёд → следующий период
    this.navigateTimeline(deltaX > 0 ? 1 : -1);
    return true;
  };

  handleSheetPeriodWheel = (e) => {
    const dx = (e.shiftKey && !e.deltaX && e.deltaY)
      ? e.deltaY
      : e.deltaX;
    const absX = Math.abs(dx);
    const absY = Math.abs(e.deltaY);
    if (absX < 4 || absX < absY * 1.15) {
      this._periodNavWheelAcc = 0;
      return;
    }
    if (!this.canNavigatePeriodByHorizontal(dx)) {
      this._periodNavWheelAcc = 0;
      return;
    }
    e.preventDefault();
    this._periodNavWheelAcc = (this._periodNavWheelAcc || 0) + dx;
    if (Math.abs(this._periodNavWheelAcc) < 70) return;
    this.tryNavigatePeriodByGesture(this._periodNavWheelAcc);
  };

  handleSheetPeriodTouchStart = (e) => {
    if (!e.touches || e.touches.length !== 1) return;
    this._periodTouchStartX = e.touches[0].clientX;
    this._periodTouchStartY = e.touches[0].clientY;
    this._periodTouchMoved = false;
  };

  handleSheetPeriodTouchMove = (e) => {
    if (this._periodTouchStartX == null || !e.touches || !e.touches[0]) return;
    const dx = e.touches[0].clientX - this._periodTouchStartX;
    const dy = e.touches[0].clientY - this._periodTouchStartY;
    if (Math.abs(dx) > 12 || Math.abs(dy) > 12) {
      this._periodTouchMoved = true;
    }
  };

  handleSheetPeriodTouchEnd = (e) => {
    if (this._periodTouchStartX == null || !e.changedTouches || !e.changedTouches[0]) {
      this._periodTouchStartX = null;
      return;
    }
    const t = e.changedTouches[0];
    const dx = t.clientX - this._periodTouchStartX;
    const dy = t.clientY - this._periodTouchStartY;
    this._periodTouchStartX = null;
    this._periodTouchStartY = null;
    if (!this._periodTouchMoved) return;
    if (Math.abs(dx) < 56 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
    // Палец влево (dx < 0) → следующий период: передаём положительный delta как «вперёд»
    this.tryNavigatePeriodByGesture(-dx);
  };

  handleDocumentMouseMove = (e) => {
    if (!this.state.sectionDrag) return;
    // elementFromPoint: при зажатой кнопке e.target часто остаётся на уголке,
    // а сжатие идёт внутри той же td — нужен элемент под курсором.
    const fromPoint = this.timetableCellFromPoint(e.clientX, e.clientY);
    const fromTarget = e.target && e.target.closest
      ? e.target.closest('[data-timetable-cell]')
      : null;
    const cell = this.preferTimetableSlotCell(fromPoint || fromTarget);
    if (!cell) return;
    const day = cell.getAttribute('data-day');
    const startInterval = Number(cell.getAttribute('data-interval'));
    const rowspan = Number(cell.getAttribute('data-rowspan') || 1);
    if (!day || Number.isNaN(startInterval)) return;
    const rect = cell.getBoundingClientRect();
    const mapped = intervalFromClientYInSpan(
      e.clientY,
      rect.top,
      rect.height,
      startInterval,
      rowspan,
    );
    this.applySectionDropTarget(day, mapped);
  };

  timetableCellFromPoint = (clientX, clientY) => {
    if (typeof document === 'undefined' || !document.elementFromPoint) return null;
    const el = document.elementFromPoint(clientX, clientY);
    if (!el || typeof el.closest !== 'function') return null;
    return this.preferTimetableSlotCell(el.closest('[data-timetable-cell]'));
  };

  preferTimetableSlotCell = (el) => {
    if (!el || typeof el.closest !== 'function') return el;
    return el.closest('td[data-timetable-cell]') || el;
  };

  applySectionDropTarget = (day, mappedInterval) => {
    const { sectionDrag, sectionDropTarget, sectionDragMoved } = this.state;
    if (!sectionDrag) return;
    const selectedDate = moment(day, 'YYYY-MM-DD');
    if (selectedDate.isBefore(moment().startOf('day'))) return;

    let interval = Number(mappedInterval);
    if (Number.isNaN(interval)) return;
    const mode = sectionDrag.mode || 'move';
    if (mode === 'resize-end' || mode === 'resize-start') {
      if (sectionDrag.kind === 'booking') {
        interval = clampResizeDropInterval(
          mode,
          interval,
          sectionDrag.blockStartInterval,
          sectionDrag.blockEndInterval,
        );
      } else {
        const bounds = getOccurrenceIntervalBounds(sectionDrag.event);
        if (bounds) {
          interval = clampResizeDropInterval(
            mode,
            interval,
            bounds.startInterval,
            bounds.endInterval,
          );
        }
      }
    }

    if (sectionDrag.kind === 'booking') {
      if (this.getSectionOccupancyAt(day, interval)) return;
      const daySchedule = this.state.schedule && this.state.schedule[day];
      const otherId = getIntervalBookingMergeId(daySchedule && daySchedule[interval]);
      const selfId = String(
        sectionDrag.booking.id != null ? sectionDrag.booking.id : sectionDrag.booking.Id,
      );
      if (otherId && otherId !== selfId) return;
    }

    if (
      sectionDropTarget
      && sectionDropTarget.day === day
      && Number(sectionDropTarget.interval) === Number(interval)
      && sectionDragMoved
    ) {
      return;
    }

    this.setState({
      sectionDropTarget: { day, interval },
      sectionDragMoved: true,
    });
  };

  handleDocumentMouseUp = (e) => {
    if (this.state.sectionDrag) {
      this.finishSectionDrag();
      return;
    }
    if (this.state.isSelecting) {
      const target = e.target;
      if (!target.closest('[data-timetable-cell]')) {
        this.cancelSelection();
      }
    }
  }

  handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      this.clearPendingCellAction();
      if (this.state.sectionDrag) {
        this.clearSectionDrag();
        return;
      }
      if (this.state.isSelecting) {
      this.cancelSelection();
      }
    }
  }

  // Вычисляет выделенный диапазон интервалов
  getSelectedIntervals = () => {
    const { selectionStart, selectionEnd } = this.state;
    if (!selectionStart || !selectionEnd) return [];
    
    // Выделение должно быть в пределах одного дня и одной части
    if (selectionStart.day !== selectionEnd.day || selectionStart.part !== selectionEnd.part) {
      return [];
    }
    
    const minInterval = Math.min(selectionStart.interval, selectionEnd.interval);
    const maxInterval = Math.max(selectionStart.interval, selectionEnd.interval);
    
    const intervals = [];
    for (let i = minInterval; i <= maxInterval; i++) {
      intervals.push(i);
    }
    
    return intervals;
  }

  // Проверяет, выделена ли ячейка (day — дата в формате YYYY-MM-DD)
  isCellSelected = (interval, part, day) => {
    const { selectionStart, selectionEnd, isSelecting } = this.state;
    if (!isSelecting || !selectionStart || !selectionEnd) return false;

    const selectedIntervals = this.getSelectedIntervals();

    return selectedIntervals.includes(interval) &&
           selectionStart.part === part &&
           selectionStart.day === day;
  }

  // Отмена выделения при клике вне ячеек
  cancelSelection = () => {
    this.setState({
      selectionStart: null,
      selectionEnd: null,
      isSelecting: false,
    });
  }

  clearSectionDrag = () => {
    this.setState({
      sectionDrag: null,
      sectionDropTarget: null,
      sectionDragMoved: false,
    });
  }

  /** После DnD mouseup браузер ещё шлёт click — не открывать аренду/диалог повторно. */
  suppressNextCellClick = () => {
    this._suppressCellClick = true;
    window.setTimeout(() => {
      this._suppressCellClick = false;
    }, 0);
  }

  intervalToLocalMoment = (day, interval) => {
    const hours = Math.floor(Number(interval) / 2);
    const minutes = Number(interval) % 2 === 0 ? 0 : 30;
    return moment(day, 'YYYY-MM-DD').hour(hours).minute(minutes).second(0).millisecond(0);
  };

  /** Конец 30-мин слота (exclusive end для API). */
  intervalEndLocalMoment = (day, interval) =>
    this.intervalToLocalMoment(day, interval).add(30, 'minutes');

  getEventDurationMinutes = (event) => {
    if (!event) return 60;
    const start = this.parseOccurrenceMoment(event.startAt);
    const end = this.parseOccurrenceMoment(event.endAt);
    if (start.isValid() && end.isValid() && end.isAfter(start)) {
      return Math.max(30, end.diff(start, 'minutes'));
    }
    return 60;
  };

  pointerRatioYInTarget = (e) => {
    const el = e && e.currentTarget;
    if (!el || typeof el.getBoundingClientRect !== 'function') return 0.5;
    const rect = el.getBoundingClientRect();
    if (!rect.height) return 0.5;
    return Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
  };

  sectionDragModeForCell = (occOverlay, day, interval, e, spanRowspan, handle) => {
    if (!occOverlay || !occOverlay.event) return 'move';
    const rowspan = spanRowspan != null
      ? spanRowspan
      : ((getOccurrenceIntervalBounds(occOverlay.event) || {}).cellCount || 1);
    if (rowspan > 1 || handle) {
      return resolveMergedBlockDragMode(this.pointerRatioYInTarget(e), rowspan, handle);
    }
    const bounds = getOccurrenceIntervalBounds(occOverlay.event);
    return resolveSectionDragMode(
      bounds,
      day,
      interval,
      this.pointerRatioYInTarget(e),
    );
  };

  sectionCellCursor = (occOverlay, day, interval, spanRowspan) => {
    if (!occOverlay || !occOverlay.event) return undefined;
    const rowspan = spanRowspan != null
      ? Number(spanRowspan)
      : ((getOccurrenceIntervalBounds(occOverlay.event) || {}).cellCount || 1);
    if (rowspan > 1) return 'grab';
    const bounds = getOccurrenceIntervalBounds(occOverlay.event);
    if (!bounds) return 'grab';
    const isFirst =
      day === bounds.startDay && Number(interval) === Number(bounds.startInterval);
    const isLast =
      day === bounds.endDay && Number(interval) === Number(bounds.endInterval);
    if (isFirst || isLast) return sectionDragCursorForMode('resize-end');
    return 'grab';
  };

  /**
   * Визуал ячейки: свободные — лёгкая заливка; занятые (аренда/секция/персоналка) —
   * единый rounded chip. Прошлое — muted gray.
   */
  resolveCellVisualStyle = (item, occOverlay, { isPast = false } = {}) => {
    const { color, isBooked } = getTipData(item || {});
    const hasOcc = !!(occOverlay && occOverlay.label);
    const occupied = !!(isBooked || hasOcc);

    if (!occupied) {
      const tone = color === 'grey' ? TIMETABLE_CHIP.closed : TIMETABLE_CHIP.free;
      return {
        occupied: false,
        backgroundColor: tone.bg,
        color: tone.fg,
        borderRadius: 0,
        boxShadow: 'none',
        margin: 0,
      };
    }

    let tone = TIMETABLE_CHIP.unpaid;
    if (hasOcc) {
      const kind = (occOverlay && occOverlay.kind) || 'section_occurrence';
      if (kind === 'personal_training') {
        tone = TIMETABLE_CHIP.personal;
      } else {
        const groupId = (occOverlay.event && occOverlay.event.sectionGroupId) != null
          ? occOverlay.event.sectionGroupId
          : null;
        tone = toneForSectionGroupId(groupId) || TIMETABLE_CHIP.section;
      }
    } else if (color === 'red') {
      tone = TIMETABLE_CHIP.paid;
    } else if (color === 'darkgrey') {
      tone = TIMETABLE_CHIP.admin;
    } else if (color === 'yellow') {
      tone = TIMETABLE_CHIP.unpaid;
    }

    if (isPast) {
      tone = TIMETABLE_CHIP.past;
    }

    return {
      occupied: true,
      backgroundColor: tone.bg,
      color: tone.fg,
      borderRadius: 8,
      boxShadow: 'none',
      margin: 2,
    };
  };

  formatChipTime = (occOverlay, interval) => {
    if (occOverlay && occOverlay.event && occOverlay.event.startAt) {
      const m = this.parseOccurrenceMoment(occOverlay.event.startAt);
      if (m && m.isValid()) return m.format('HH:mm');
    }
    if (interval != null && interval !== '') {
      return intervalSlotTimeRange(interval).start;
    }
    return '';
  };

  renderChipLabel = (title, time, textColor, wrap = false) => {
    if (!title && !time) return ' ';
    return (
      <div
        style={{
          fontSize: wrap ? 11 : 10,
          lineHeight: 1.2,
          padding: wrap ? '5px 6px 8px' : '3px 5px',
          textAlign: 'left',
          whiteSpace: wrap ? 'normal' : 'nowrap',
          overflow: 'hidden',
          textOverflow: wrap ? 'clip' : 'ellipsis',
          width: '100%',
          color: textColor,
          pointerEvents: 'none',
        }}
      >
        {title ? (
          <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title}
          </div>
        ) : null}
        {time ? (
          <div
            style={{
              fontWeight: 500,
              fontSize: wrap ? 10 : 9,
              opacity: 0.92,
              marginTop: title ? 1 : 0,
            }}
          >
            {time}
          </div>
        ) : null}
      </div>
    );
  };

  intervalFromPointerInSpan = (e, startInterval, rowspan) => {
    const el = e && e.currentTarget;
    const rect = el && typeof el.getBoundingClientRect === 'function'
      ? el.getBoundingClientRect()
      : null;
    if (!rect) return Number(startInterval);
    return intervalFromClientYInSpan(
      e.clientY,
      rect.top,
      rect.height,
      startInterval,
      rowspan,
    );
  };

  isIntervalInDragPreview = (day, interval) => {
    const { sectionDrag, sectionDropTarget } = this.state;
    if (!sectionDrag || !sectionDropTarget || sectionDropTarget.day !== day) return false;
    const mode = sectionDrag.mode || 'move';
    const dropIv = Number(sectionDropTarget.interval);
    if (sectionDrag.kind === 'booking') {
      const count = sectionDrag.durationSlots || 1;
      const start = Number(sectionDrag.blockStartInterval);
      const end = Number(sectionDrag.blockEndInterval);
      if (mode === 'move') {
        return interval >= dropIv && interval < dropIv + count;
      }
      if (mode === 'resize-end') {
        const from = Math.min(start, dropIv);
        const to = Math.max(start, dropIv);
        return interval >= from && interval <= to;
      }
      if (mode === 'resize-start') {
        const from = Math.min(dropIv, end);
        const to = Math.max(dropIv, end);
        return interval >= from && interval <= to;
      }
      return dropIv === Number(interval);
    }
    const bounds = getOccurrenceIntervalBounds(sectionDrag.event);
    const count = (bounds && bounds.cellCount) || 1;
    if (mode === 'move') {
      return interval >= dropIv && interval < dropIv + count;
    }
    if (mode === 'resize-end') {
      const start = bounds ? Number(bounds.startInterval) : Number(sectionDrag.originInterval);
      const from = Math.min(start, dropIv);
      const to = Math.max(start, dropIv);
      return interval >= from && interval <= to;
    }
    if (mode === 'resize-start') {
      const end = bounds ? Number(bounds.endInterval) : Number(sectionDrag.originInterval);
      const from = Math.min(dropIv, end);
      const to = Math.max(dropIv, end);
      return interval >= from && interval <= to;
    }
    return dropIv === Number(interval);
  };

  isDragSourceBlock = (day, interval) => {
    const { sectionDrag } = this.state;
    if (!sectionDrag || sectionDrag.originDay !== day) return false;
    if (sectionDrag.kind === 'booking') {
      return Number(interval) === Number(sectionDrag.blockStartInterval);
    }
    const bounds = getOccurrenceIntervalBounds(sectionDrag.event);
    if (!bounds) return Number(sectionDrag.originInterval) === Number(interval);
    return Number(interval) === Number(bounds.startInterval);
  };

  getMergedResizePreview = (day, startInterval, rowspan) => {
    const { sectionDrag, sectionDropTarget } = this.state;
    if (!sectionDrag || !sectionDropTarget) return null;
    const mode = sectionDrag.mode || 'move';
    if (mode !== 'resize-end' && mode !== 'resize-start') return null;
    if (!this.isDragSourceBlock(day, startInterval)) return null;
    let blockStart;
    let blockEnd;
    if (sectionDrag.kind === 'booking') {
      blockStart = sectionDrag.blockStartInterval;
      blockEnd = sectionDrag.blockEndInterval;
    } else {
      const bounds = getOccurrenceIntervalBounds(sectionDrag.event);
      if (!bounds) return null;
      blockStart = bounds.startInterval;
      blockEnd = bounds.endInterval;
    }
    return mergedBlockResizePreview(
      mode,
      blockStart,
      blockEnd,
      sectionDropTarget.interval,
      rowspan,
    );
  };

  getDayRowspans = (day, hourArray) => {
    const keys = buildDayMergeKeys(day, hourArray, this.getSectionOccupancyAt);
    return buildColumnRowspans(keys);
  };

  shortSectionLabel = (occOverlay) => {
    if (!occOverlay) return '';
    const event = occOverlay.event || {};
    const kind = occOverlay.kind || event.kind || 'section_occurrence';
    if (kind === 'personal_training') {
      return event.sectionName
        ? `Перс.: ${event.sectionName}`
        : (occOverlay.label || 'Персональная');
    }
    return (
      event.sectionGroupName
      || occOverlay.label
      || event.sectionName
      || 'Секция'
    );
  };

  getSectionOccupancyAt = (day, interval) => {
    const raw = this.state.sectionOccurrenceMap[`${day}::${interval}`];
    if (!raw) return null;
    if (typeof raw === 'string') return { label: raw, kind: 'section_occurrence' };
    return raw;
  };

  normalizeOccurrenceEvent = (occ) => normalizeOccurrenceEventLib(occ);

  loadTrainerDirectory = async () => {
    const { sportComplexId } = this.props;
    if (sportComplexId == null) {
      this.setState({ trainerMap: {}, trainersError: '' });
      return;
    }
    try {
      let organizationId = this.state.organizationId;
      if (!organizationId) {
        const org = await api.getOrganizationByComplex(sportComplexId);
        organizationId = organizationIdFrom(org);
      }
      if (!organizationId) {
        this.setState({
          trainerMap: {},
          trainersError: 'У клуба нет организации — сотрудников выбрать нельзя',
        });
        return;
      }
      const members = normalizeListResponse(await api.getOrganizationMembers(organizationId));
      const options = orgMembersToTrainerOptions(members, sportComplexId);
      const trainerMap = {};
      options.forEach((u) => {
        if (u.id != null) trainerMap[Number(u.id)] = u;
      });
      this.setState({ trainerMap, trainersError: '', organizationId });
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Timetable: сотрудники организации для тренера', e);
      }
      this.setState({
        trainerMap: {},
        trainersError: (e && e.message) || 'Не удалось загрузить сотрудников организации',
      });
    }
  };

  trainerSelectOptions = () => {
    const map = this.state.trainerMap || {};
    return Object.keys(map)
      .map((key) => map[key])
      .filter(Boolean)
      .sort((a, b) =>
        String(a.label || a.name || '').localeCompare(String(b.label || b.name || ''), 'ru'),
      );
  };

  trainerLabelById = (trainerId) => {
    if (trainerId == null || trainerId === '') return null;
    const u = this.state.trainerMap[Number(trainerId)];
    if (!u) return null;
    return u.label || formatUserLabel(u) || null;
  };

  toDatetimeParts = (raw) => {
    const m = this.parseOccurrenceMoment(raw);
    if (!m.isValid()) return { date: '', time: '' };
    return { date: m.format('YYYY-MM-DD'), time: m.format('HH:mm') };
  };

  loadComplexClientsForPicker = async (sectionId = null) => {
    const { sportComplexId } = this.props;
    if (sportComplexId == null) {
      this.setState({ complexClientOptions: [], organizationId: null });
      return;
    }
    this.setState({ complexClientsLoading: true });
    try {
      let organizationId = this.state.organizationId;
      if (!organizationId) {
        try {
          const org = await api.getOrganizationByComplex(sportComplexId);
          organizationId = organizationIdFrom(org);
        } catch (e) {
          organizationId = null;
        }
      }
      let pickerItems = [];
      if (organizationId) {
        try {
          pickerItems = normalizeListResponse(await api.getOrganizationClientsPicker(organizationId));
        } catch (e) {
          pickerItems = [];
        }
        if (!pickerItems.length) {
          try {
            pickerItems = normalizeListResponse(await api.getOrganizationClients(organizationId));
          } catch (e) {
            pickerItems = [];
          }
        }
      }
      let paidUserIds = new Set();
      if (sectionId) {
        try {
          const abonements = normalizeListResponse(
            await api.getAbonements({ complexId: sportComplexId, sectionId }),
          );
          paidUserIds = collectPaidUserIds(abonements, sectionId);
        } catch (e) {
          paidUserIds = new Set();
        }
      }
      const orgOptions = mapOrgClientsToPickerOptions(pickerItems, { paidUserIds });
      const settled = await Promise.allSettled([
        api.getSectionRequests({ complexId: sportComplexId }),
        api.getAbonements({ complexId: sportComplexId }),
        api.getPersonalTrainings({ complexId: sportComplexId }),
        api.getComplexSectionGroupMembers(sportComplexId),
      ]);
      const pull = (idx) => {
        const r = settled[idx];
        return r.status === 'fulfilled' ? normalizeListResponse(r.value) : [];
      };
      const requests = pull(0);
      const abonements = pull(1);
      const personalTrainings = pull(2);
      const groupMembers = pull(3);
      const userMap = buildClientsUserMap({
        requests,
        abonements,
        personalTrainings,
        groupMembers,
      });
      const sectionOptions = buildComplexClientOptions({
        requests,
        abonements,
        personalTrainings,
        groupMembers,
        userMap,
        sectionId,
      });
      const options = mergePickerOptions(orgOptions, sectionOptions);
      this.setState({
        complexClientOptions: options,
        organizationId: organizationId || null,
        complexClientsLoading: false,
      });
    } catch (e) {
      this.setState({ complexClientOptions: [], complexClientsLoading: false });
    }
  };

  bumpOccurrenceRosterEpoch = () => {
    this._occurrenceRosterEpoch += 1;
    return this._occurrenceRosterEpoch;
  };

  isStaleOccurrenceRoster = (requestedEvent, loadEpoch) =>
    isOccurrenceRosterResponseStale({
      requestedOccurrenceId: requestedEvent && requestedEvent.id,
      requestedSectionId: requestedEvent && requestedEvent.sectionId,
      currentDialog: this.state.sectionEventDialogData,
      dialogOpen: this.state.sectionEventDialogOpen,
      loadEpoch,
      currentEpoch: this._occurrenceRosterEpoch,
    });

  openSectionEventDialog = (cellData) => {
    if (!cellData) return;
    const event = cellData.event || cellData;
    const startParts = this.toDatetimeParts(event.startAt);
    const endParts = this.toDatetimeParts(event.endAt);
    const epoch = this.bumpOccurrenceRosterEpoch();
    this.setState({
      sectionEventDialogOpen: true,
      sectionEventDialogData: event,
      sectionEventRescheduleDate: startParts.date,
      sectionEventRescheduleTime: startParts.time,
      sectionEventRescheduleEndDate: endParts.date,
      sectionEventRescheduleEndTime: endParts.time,
      sectionEventRescheduleSaving: false,
      sectionEventRescheduleError: '',
      occurrenceRoster: [],
      occurrenceRosterLoading: false,
      occurrenceRosterError: '',
      occurrenceRosterAddUserId: '',
      occurrenceRosterSaving: false,
    }, () => {
      this.loadOccurrenceRoster(event, epoch);
      if (event && event.kind !== 'personal_training') {
        this.loadComplexClientsForPicker(event.sectionId);
      }
    });
  };

  closeSectionEventDialog = () => {
    this.bumpOccurrenceRosterEpoch();
    this.setState({
      sectionEventDialogOpen: false,
      sectionEventDialogData: null,
      sectionEventRescheduleDate: '',
      sectionEventRescheduleTime: '',
      sectionEventRescheduleEndDate: '',
      sectionEventRescheduleEndTime: '',
      sectionEventRescheduleSaving: false,
      sectionEventRescheduleError: '',
      occurrenceRoster: [],
      occurrenceRosterLoading: false,
      occurrenceRosterError: '',
      occurrenceRosterAddUserId: '',
      occurrenceRosterSaving: false,
    });
  };

  loadOccurrenceRoster = async (event, epoch) => {
    const ev = event || this.state.sectionEventDialogData;
    const loadEpoch = epoch != null ? epoch : this._occurrenceRosterEpoch;
    if (loadEpoch !== this._occurrenceRosterEpoch || !this.state.sectionEventDialogOpen) {
      return;
    }
    if (!ev || ev.kind === 'personal_training' || ev.sectionId == null || ev.id == null) {
      this.setState({ occurrenceRoster: [], occurrenceRosterLoading: false });
      return;
    }
    if (this.isStaleOccurrenceRoster(ev, loadEpoch)) return;
    this.setState({ occurrenceRosterLoading: true, occurrenceRosterError: '' });
    try {
      const data = await api.getSectionOccurrenceBookings(ev.sectionId, ev.id);
      if (this.isStaleOccurrenceRoster(ev, loadEpoch)) return;
      this.setState({
        occurrenceRoster: normalizeListResponse(data),
        occurrenceRosterLoading: false,
      });
    } catch (e) {
      if (this.isStaleOccurrenceRoster(ev, loadEpoch)) return;
      this.setState({
        occurrenceRoster: [],
        occurrenceRosterLoading: false,
        occurrenceRosterError: formatBookingErrorForUser(e) || (e && e.message) || 'Не удалось загрузить участников',
      });
    }
  };

  markOccurrenceAttendance = async (bookingId, statusName) => {
    const targetEv = this.state.sectionEventDialogData;
    const epoch = this._occurrenceRosterEpoch;
    this.setState({ occurrenceRosterSaving: true, occurrenceRosterError: '' });
    try {
      await api.updateSectionOccurrenceAttendance(bookingId, { statusName });
      if (this.isStaleOccurrenceRoster(targetEv, epoch)) return;
      await this.loadOccurrenceRoster(targetEv, epoch);
    } catch (e) {
      if (this.isStaleOccurrenceRoster(targetEv, epoch)) return;
      this.setState({
        occurrenceRosterError: formatBookingErrorForUser(e) || (e && e.message) || 'Не удалось отметить явку',
      });
    } finally {
      if (!this.isStaleOccurrenceRoster(targetEv, epoch)) {
        this.setState({ occurrenceRosterSaving: false });
      }
    }
  };

  addOccurrenceRosterBooking = async () => {
    const ev = this.state.sectionEventDialogData;
    const epoch = this._occurrenceRosterEpoch;
    const clientRef = clientRefFromPickerValue(
      this.state.complexClientOptions,
      this.state.occurrenceRosterAddUserId,
    );
    if (!ev || ev.id == null) return;
    if (!hasPickerClient(clientRef)) {
      this.setState({ occurrenceRosterError: 'Выберите клиента' });
      return;
    }
    this.setState({ occurrenceRosterSaving: true, occurrenceRosterError: '' });
    try {
      await api.createSectionOccurrenceBooking({
        sectionOccurrenceId: ev.id,
        ...sectionClientWriteFields(clientRef),
      });
      if (this.isStaleOccurrenceRoster(ev, epoch)) return;
      this.setState((prev) => {
        const current = prev.sectionEventDialogData;
        if (!current || Number(current.id) !== Number(ev.id)) {
          return {};
        }
        const booked = current.bookedCount != null ? Number(current.bookedCount) : NaN;
        return {
          occurrenceRosterAddUserId: '',
          sectionEventDialogData: {
            ...current,
            bookedCount: Number.isNaN(booked) ? 1 : booked + 1,
          },
        };
      });
      await this.loadOccurrenceRoster(ev, epoch);
      if (ev.sectionId) {
        this.loadComplexClientsForPicker(ev.sectionId);
      }
      this.showSnackbar('Клиент записан на занятие');
    } catch (e) {
      if (this.isStaleOccurrenceRoster(ev, epoch)) return;
      this.setState({
        occurrenceRosterError: formatBookingErrorForUser(e) || (e && e.message) || 'Не удалось записать клиента',
      });
    } finally {
      if (!this.isStaleOccurrenceRoster(ev, epoch)) {
        this.setState({ occurrenceRosterSaving: false });
      }
    }
  };

  applySectionReschedule = async (event, startLocal, options = {}) => {
    if (!event || !startLocal || !startLocal.isValid()) {
      throw new Error('Некорректная дата/время начала');
    }
    let endLocal = options.endLocal;
    if (!endLocal || !endLocal.isValid()) {
      const durationMin = options.durationMin != null
        ? options.durationMin
        : this.getEventDurationMinutes(event);
      endLocal = startLocal.clone().add(durationMin, 'minutes');
    }
    if (!endLocal.isAfter(startLocal)) {
      throw new Error('Конец должен быть позже начала');
    }
    const durationMin = Math.max(1, endLocal.diff(startLocal, 'minutes'));
    const kind = event.kind;
    if (kind === 'personal_training') {
      await api.updatePersonalTraining(event.id, {
        sectionId: event.sectionId,
        startAt: startLocal.toISOString(),
        endAt: endLocal.toISOString(),
        durationMinutes: durationMin,
        playgroundId: event.playgroundId,
        trainerId: event.trainerId,
      });
    } else {
      const sectionId = event.sectionId;
      if (sectionId == null || event.id == null) {
        throw new Error('Нельзя перенести: нет sectionId или id занятия');
      }
      await api.updateSectionOccurrence(sectionId, event.id, {
        startAt: startLocal.toISOString(),
        endAt: endLocal.toISOString(),
        durationMinutes: durationMin,
      });
    }
  };

  saveSectionEventReschedule = async () => {
    const {
      sectionEventDialogData,
      sectionEventRescheduleDate,
      sectionEventRescheduleTime,
      sectionEventRescheduleEndDate,
      sectionEventRescheduleEndTime,
    } = this.state;
    if (
      !sectionEventDialogData
      || !sectionEventRescheduleDate
      || !sectionEventRescheduleTime
      || !sectionEventRescheduleEndDate
      || !sectionEventRescheduleEndTime
    ) {
      this.setState({ sectionEventRescheduleError: 'Укажите начало и конец занятия' });
      return;
    }
    const startLocal = moment(
      `${sectionEventRescheduleDate} ${sectionEventRescheduleTime}`,
      'YYYY-MM-DD HH:mm',
      true,
    );
    const endLocal = moment(
      `${sectionEventRescheduleEndDate} ${sectionEventRescheduleEndTime}`,
      'YYYY-MM-DD HH:mm',
      true,
    );
    if (!startLocal.isValid() || !endLocal.isValid()) {
      this.setState({ sectionEventRescheduleError: 'Некорректная дата/время' });
      return;
    }
    if (!endLocal.isAfter(startLocal)) {
      this.setState({ sectionEventRescheduleError: 'Конец должен быть позже начала' });
      return;
    }

    this.setState({ sectionEventRescheduleSaving: true, sectionEventRescheduleError: '' });
    try {
      await this.applySectionReschedule(sectionEventDialogData, startLocal, { endLocal });
      this.closeSectionEventDialog();
      this.showSnackbar('Занятие сохранено');
      await this.refreshSectionOverlay();
    } catch (e) {
      this.setState({
        sectionEventRescheduleSaving: false,
        sectionEventRescheduleError: (e && e.message) || 'Не удалось сохранить занятие',
      });
    }
  };

  startSectionDrag = (e, choosenDay, interval, occOverlay, options = {}) => {
    if (!occOverlay || !occOverlay.event) return false;
    e.preventDefault();
    e.stopPropagation();
    const event = occOverlay.event;
    const bounds = getOccurrenceIntervalBounds(event);
    const rowspan = options.rowspan != null
      ? options.rowspan
      : ((bounds && bounds.cellCount) || 1);
    const mode = this.sectionDragModeForCell(
      occOverlay,
      choosenDay,
      interval,
      e,
      rowspan,
      options.handle,
    );
    let originInterval = interval;
    if (bounds) {
      if (mode === 'resize-end') originInterval = bounds.endInterval;
      else originInterval = bounds.startInterval;
    }
    const fixedStartAt = this.parseOccurrenceMoment(event.startAt);
    const fixedEndAt = this.parseOccurrenceMoment(event.endAt);
    this.setState({
      sectionDrag: {
        event,
        mode,
        durationMin: this.getEventDurationMinutes(event),
        originDay: choosenDay,
        originInterval,
        fixedStartAt: fixedStartAt.isValid() ? fixedStartAt.toISOString() : null,
        fixedEndAt: fixedEndAt.isValid() ? fixedEndAt.toISOString() : null,
      },
      sectionDropTarget: { day: choosenDay, interval: originInterval },
      sectionDragMoved: false,
      isSelecting: false,
      selectionStart: null,
      selectionEnd: null,
    });
    return true;
  };

  workingHoursTypeToNumber = (type) => {
    const raw = type != null ? type : this.props.currentWorkingHoursTypes;
    if (raw === 'half' || raw === '1' || raw === 1) return 1;
    if (raw === 'third' || raw === '2' || raw === 2) return 2;
    return 0;
  };

  partToNumber = (part) => {
    if (part === 'left' || part === 'Left' || part === 1) return 1;
    if (part === 'right' || part === 'Right' || part === 2) return 2;
    if (part === 'middle' || part === 'Middle' || part === 4) return 4;
    return 0;
  };

  startBookingDrag = (e, choosenDay, interval, booking, part, intervalData, options = {}) => {
    if (!booking || (booking.id == null && booking.Id == null)) return false;
    e.preventDefault();
    e.stopPropagation();
    const rowspan = options.rowspan || 1;
    const mode = resolveMergedBlockDragMode(
      this.pointerRatioYInTarget(e),
      rowspan,
      options.handle,
    );
    const blockStart = Number(interval);
    const blockEnd = blockStart + rowspan - 1;
    const originInterval = mode === 'resize-end' ? blockEnd : blockStart;
    this.setState({
      sectionDrag: {
        kind: 'booking',
        booking,
        part,
        intervalData,
        mode,
        durationSlots: rowspan,
        blockStartInterval: blockStart,
        blockEndInterval: blockEnd,
        originDay: choosenDay,
        originInterval,
      },
      sectionDropTarget: { day: choosenDay, interval: originInterval },
      sectionDragMoved: false,
      isSelecting: false,
      selectionStart: null,
      selectionEnd: null,
    });
    return true;
  };

  applyBookingReschedule = async (booking, day, startInterval, endIntervalExclusive, part) => {
    const bookingId = Number(booking.id != null ? booking.id : booking.Id);
    if (!bookingId) {
      throw new Error('У брони нет id');
    }
    const typeValue = booking.type != null
      ? this.workingHoursTypeToNumber(booking.type)
      : this.workingHoursTypeToNumber();
    const partValue = this.partToNumber(part);
    const intervals = times(endIntervalExclusive - startInterval, (index) => ({
      interval: startInterval + index,
      part: partValue,
      type: typeValue,
    }));
    if (!intervals.length) {
      throw new Error('Пустой диапазон аренды');
    }
    await api.updateBooking(bookingId, {
      id: bookingId,
      date: day,
      playgroundId: booking.playgroundId || this.props.playgroundId,
      intervals,
      price: booking.price || 0,
      sportType: booking.sportType != null ? booking.sportType : booking.SportType,
      type: typeValue,
      userId: booking.userId || (booking.user && booking.user.id) || null,
      details: booking.details || booking.Details || null,
    });
  };

  openBookingDialogFromDrag = (sectionDrag) => {
    if (!sectionDrag) return;
    this.setState({
      intervalData: sectionDrag.intervalData,
      choosenDay: sectionDrag.originDay,
      chosenPart: sectionDrag.part,
      showDialog: true,
    });
  };

  finishBookingDrag = async (sectionDrag, drop, moved) => {
    const booking = sectionDrag.booking;
    if (!moved || !drop) {
      this.openBookingDialogFromDrag(sectionDrag);
      return;
    }
    if (drop.day === sectionDrag.originDay && Number(drop.interval) === Number(sectionDrag.originInterval)) {
      this.openBookingDialogFromDrag(sectionDrag);
      return;
    }
    const dropDay = moment(drop.day, 'YYYY-MM-DD');
    if (dropDay.isBefore(moment().startOf('day'))) {
      this.showSnackbar('Нельзя перенести аренду в прошлое');
      return;
    }
    const occ = this.getSectionOccupancyAt(drop.day, drop.interval);
    if (occ) {
      this.showSnackbar(`Слот уже занят: ${occ.label || 'занятие секции'}`);
      return;
    }

    const mode = sectionDrag.mode || 'move';
    const startBound = Number(sectionDrag.blockStartInterval);
    const endBound = Number(sectionDrag.blockEndInterval);
    const count = sectionDrag.durationSlots || (endBound - startBound + 1);
    let startInterval;
    let endExclusive;
    if (mode === 'resize-start') {
      startInterval = Number(drop.interval);
      endExclusive = endBound + 1;
    } else if (mode === 'resize-end') {
      startInterval = startBound;
      endExclusive = Number(drop.interval) + 1;
    } else {
      startInterval = Number(drop.interval);
      endExclusive = startInterval + count;
    }
    if (endExclusive <= startInterval) {
      this.showSnackbar('Конец должен быть позже начала');
      return;
    }
    if (this.isPastDay(drop.day) && startInterval != null) {
      this.showSnackbar('Нельзя перенести аренду в прошлое');
      return;
    }

    try {
      await this.applyBookingReschedule(
        booking,
        drop.day,
        startInterval,
        endExclusive,
        sectionDrag.part,
      );
      const startLocal = this.intervalToLocalMoment(drop.day, startInterval);
      const msg = mode === 'resize-start'
        ? `Начало аренды: ${startLocal.format('DD.MM HH:mm')}`
        : mode === 'resize-end'
          ? `Конец аренды: ${this.intervalToLocalMoment(drop.day, endExclusive - 1).add(30, 'minutes').format('HH:mm')}`
          : `Аренда перенесена на ${startLocal.format('DD.MM HH:mm')}`;
      this.showSnackbar(msg);
      this.loadWeekData(this.state.weekStart, this.props.currentWorkingHoursTypes);
      this.loadMonthData(this.state.currentMonth, this.props.currentWorkingHoursTypes);
    } catch (err) {
      this.showSnackbar(formatBookingErrorForUser(err) || 'Не удалось изменить аренду');
    }
  };

  finishSectionDrag = async () => {
    const { sectionDrag, sectionDropTarget, sectionDragMoved } = this.state;
    if (!sectionDrag || this._finishingSectionDrag) return;
    this._finishingSectionDrag = true;

    const drop = sectionDropTarget;
    const moved = sectionDragMoved;
    this.clearSectionDrag();
    this.suppressNextCellClick();

    try {
      if (sectionDrag.kind === 'booking') {
        await this.finishBookingDrag(sectionDrag, drop, moved);
        return;
      }

      const {
        event,
        mode,
        durationMin,
        originDay,
        originInterval,
        fixedStartAt,
        fixedEndAt,
      } = sectionDrag;

      if (!moved || !drop) {
        this.openSectionEventDialog({ event, label: event.label, kind: event.kind });
        return;
      }

      if (drop.day === originDay && Number(drop.interval) === Number(originInterval)) {
        this.openSectionEventDialog({ event, label: event.label, kind: event.kind });
        return;
      }

      const dropDay = moment(drop.day, 'YYYY-MM-DD');
      if (dropDay.isBefore(moment().startOf('day'))) {
        this.showSnackbar('Нельзя перенести занятие в прошлое');
        return;
      }

      const existing = this.getSectionOccupancyAt(drop.day, drop.interval);
      if (
        existing
        && existing.event
        && existing.event.id != null
        && Number(existing.event.id) !== Number(event.id)
      ) {
        this.showSnackbar(`Слот уже занят: ${existing.label || 'другое занятие'}`);
        return;
      }

      let startLocal;
      let endLocal;
      const dragMode = mode || 'move';

      if (dragMode === 'resize-start') {
        startLocal = this.intervalToLocalMoment(drop.day, drop.interval);
        endLocal = this.parseOccurrenceMoment(fixedEndAt || event.endAt);
        if (!endLocal.isValid() || !startLocal.isBefore(endLocal)) {
          this.showSnackbar('Начало должно быть раньше конца');
          return;
        }
      } else if (dragMode === 'resize-end') {
        startLocal = this.parseOccurrenceMoment(fixedStartAt || event.startAt);
        endLocal = this.intervalEndLocalMoment(drop.day, drop.interval);
        if (!startLocal.isValid() || !endLocal.isAfter(startLocal)) {
          this.showSnackbar('Конец должен быть позже начала');
          return;
        }
      } else {
        startLocal = this.intervalToLocalMoment(drop.day, drop.interval);
        endLocal = startLocal.clone().add(durationMin, 'minutes');
      }

      try {
        await this.applySectionReschedule(event, startLocal, { endLocal, durationMin });
        const msg =
          dragMode === 'resize-start'
            ? `Начало: ${startLocal.format('DD.MM HH:mm')}`
            : dragMode === 'resize-end'
              ? `Конец: ${endLocal.format('DD.MM HH:mm')}`
              : `Перенесено на ${startLocal.format('DD.MM HH:mm')}`;
        this.showSnackbar(msg);
        await this.refreshSectionOverlay();
      } catch (err) {
        this.showSnackbar((err && err.message) || 'Не удалось изменить занятие');
      }
    } finally {
      this._finishingSectionDrag = false;
    }
  };

  goToSectionFromEvent = () => {
    const { sectionEventDialogData } = this.state;
    const { sportComplexId, history } = this.props;
    if (!sectionEventDialogData || !sportComplexId || !history) return;
    const sid = sectionEventDialogData.sectionId;
    if (sid == null) return;
    history.push(
      `/sections/schedule?centerId=${encodeURIComponent(String(sportComplexId))}&sectionId=${encodeURIComponent(String(sid))}`,
    );
    this.closeSectionEventDialog();
  };

  notifySectionSlotBlocked = (occ) => {
    const kindLabel = occ && occ.kind === 'personal_training'
      ? 'персональная тренировка'
      : 'занятие секции';
    const name = (occ && occ.label) || 'занятие';
    this.showSnackbar(
      `Слот занят (${kindLabel}: ${name}). Аренда на это время недоступна — выберите свободный интервал.`,
    );
  };

  /** Свободный слот занят секцией/персоналкой — аренду открывать нельзя (просмотр брони аренды — можно). */
  isSectionBlockingRent = (day, interval, isBooked) => {
    if (isBooked) return false;
    return !!this.getSectionOccupancyAt(day, interval);
  };

  /** Прошедший день: свободные слоты нельзя бронировать; аренда и занятия секции — для просмотра. */
  isPastDay = (day) => moment(day, 'YYYY-MM-DD').isBefore(moment().startOf('day'));

  isPastCellDisabled = (day, isBooked, hasSectionOccupancy) =>
    this.isPastDay(day) && !isBooked && !hasSectionOccupancy;

  selectionOverlapsSectionOccupancy = (day, intervals) => {
    const list = intervals || [];
    for (let i = 0; i < list.length; i += 1) {
      const occ = this.getSectionOccupancyAt(day, list[i]);
      if (occ) return occ;
    }
    return null;
  };

  // Начало выделения (аренда) или drag занятия секции
  onCellMouseDown = (e, intervalData, choosenDay, chosenPart, interval, isBooked, spanInfo) => {
    const occ = this.getSectionOccupancyAt(choosenDay, interval);
    const rowspan = (spanInfo && spanInfo.rowspan) || 1;
    const handle = (e.target && e.target.getAttribute && e.target.getAttribute('data-resize')) || undefined;

    // Прошлое: занятие/персоналка — только карточка (без DnD и без новой аренды)
    if (this.isPastDay(choosenDay)) {
      if (occ && occ.event && !isBooked) {
        this.openSectionEventDialog(occ);
        return;
      }
      this.showSnackbar('Невозможно создать бронирование в прошлом. Доступен только просмотр.');
      return;
    }

    // Drag перенос слота секции / персоналки
    if (occ && occ.event && !isBooked) {
      this.startSectionDrag(e, choosenDay, interval, occ, { rowspan, handle });
      return;
    }

    if (isBooked) {
      const values = intervalData && intervalData.values;
      const partValue = chosenPart
        || (values && values[0] && values[0].part);
      const booking = values && values.find((v) => v.bookings && v.bookings.length)
        && values.find((v) => v.bookings && v.bookings.length).bookings[0];
      if (booking && (booking.id != null || booking.Id != null)) {
        this.startBookingDrag(e, choosenDay, interval, booking, partValue, intervalData, {
          rowspan,
          handle,
        });
        return;
      }
    }

    if (this.isSectionBlockingRent(choosenDay, interval, isBooked)) {
      this.notifySectionSlotBlocked(this.getSectionOccupancyAt(choosenDay, interval));
      return;
    }
    
    // Если клик правой кнопкой или с модификаторами, используем обычный клик
    if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) {
      this.onCellClick(intervalData, choosenDay, chosenPart, isBooked, interval);
      return;
    }
    
    e.preventDefault();
    this.setState({
      selectionStart: { interval, part: chosenPart, day: choosenDay },
      selectionEnd: { interval, part: chosenPart, day: choosenDay },
      isSelecting: true,
    });
  }

  // Обновление выделения / цели drop при движении мыши
  onCellMouseEnter = (e, intervalData, choosenDay, chosenPart, interval, spanInfo) => {
    const { isSelecting, selectionStart, sectionDrag } = this.state;
    const rowspan = (spanInfo && spanInfo.rowspan) || 1;
    const startInterval = spanInfo && spanInfo.startInterval != null
      ? spanInfo.startInterval
      : interval;
    const mappedInterval = rowspan > 1
      ? this.intervalFromPointerInSpan(e, startInterval, rowspan)
      : interval;

    if (sectionDrag) {
      this.applySectionDropTarget(choosenDay, mappedInterval);
      return;
    }

    if (!isSelecting || !selectionStart) return;
    
    // Проверяем, что дата не в прошлом
    const selectedDate = moment(choosenDay, 'YYYY-MM-DD');
    const today = moment().startOf('day');
    
    if (selectedDate.isBefore(today)) {
      // Не позволяем выделять прошлые даты
      return;
    }

    if (this.getSectionOccupancyAt(choosenDay, mappedInterval)) {
      return;
    }
    
    // Обновляем только если та же часть и тот же день
    if (selectionStart.part === chosenPart && selectionStart.day === choosenDay) {
      this.setState({
        selectionEnd: { interval: mappedInterval, part: chosenPart, day: choosenDay },
      });
    }
  }

  // Завершение выделения или DnD
  onCellMouseUp = (e, intervalData, choosenDay, chosenPart, isBooked) => {
    if (this.state.sectionDrag) {
      e.preventDefault();
      e.stopPropagation();
      this.finishSectionDrag();
      return;
    }

    const { isSelecting, selectionStart, selectionEnd } = this.state;
    if (!isSelecting) return;
    
    // Проверяем, что дата не в прошлом
    const selectedDate = moment(choosenDay, 'YYYY-MM-DD');
    const today = moment().startOf('day');
    
    if (selectedDate.isBefore(today)) {
      // Не позволяем выделять прошлые даты
      this.setState({
        isSelecting: false,
        selectionStart: null,
        selectionEnd: null,
      });
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    // Если выделен диапазон — тот же chooser, что и по клику (аренда / группа / персоналка)
    if (selectionStart && selectionEnd && 
        selectionStart.interval !== selectionEnd.interval) {
      this.clearPendingCellAction();
      const selectedIntervals = this.getSelectedIntervals();
      const sectionHit = this.selectionOverlapsSectionOccupancy(choosenDay, selectedIntervals);
      if (sectionHit) {
        this.notifySectionSlotBlocked(sectionHit);
        this.cancelSelection();
        this.setState({ isSelecting: false });
        return;
      }
      if (selectedIntervals.length > 0) {
        const minInterval = Math.min(selectionStart.interval, selectionEnd.interval);
        const maxInterval = Math.max(selectionStart.interval, selectionEnd.interval);
        const { schedule } = this.state;
        const daySchedule = schedule[choosenDay];
        const firstIntervalData = daySchedule && daySchedule[minInterval];
        
        if (firstIntervalData) {
          const partValue = firstIntervalData.values.find(v => v.part === chosenPart);
          if (partValue) {
            const selectedRange = {
              startInterval: minInterval,
              endInterval: maxInterval + 1, // exclusive
              part: chosenPart,
            };
            this.setState({
              selectionStart: null,
              selectionEnd: null,
              isSelecting: false,
            });

            if (!this.props.sportComplexId) {
              this.selectedRange = selectedRange;
            this.setState({
              intervalData: firstIntervalData,
              choosenDay,
              chosenPart,
              showDialog: true,
              });
              return;
            }

            this.openSlotActionChooser(
              firstIntervalData,
              choosenDay,
              chosenPart,
              minInterval,
              { selectedRange },
            );
            return;
          }
        }
      }
    } else {
      // Одиночный клик - обычное поведение
      const iv = selectionStart ? selectionStart.interval : undefined;
      this.onCellClick(intervalData, choosenDay, chosenPart, isBooked, iv);
    }
    
    this.setState({
      isSelecting: false,
    });
  }

  onCellClick = (intervalData, choosenDay, chosenPart, isBooked, interval) => {
    if (this._suppressCellClick) {
      this._suppressCellClick = false;
      return;
    }

    let intervalKey = interval;
    if (intervalKey == null && intervalData && intervalData.interval != null) {
      intervalKey = intervalData.interval;
    }

    // Прошлое: открываем карточку занятия/персоналки; новую аренду не создаём
    if (this.isPastDay(choosenDay)) {
      this.clearPendingCellAction();
      if (intervalKey != null && this.isSectionBlockingRent(choosenDay, intervalKey, isBooked)) {
        this.openSectionEventDialog(this.getSectionOccupancyAt(choosenDay, intervalKey));
        return;
      }
      if (isBooked) {
    this.setState({
          intervalData,
          choosenDay,
          chosenPart,
          showDialog: true,
        });
        return;
      }
      this.showSnackbar('Невозможно создать бронирование в прошлом. Доступен только просмотр.');
      return;
    }

    if (intervalKey != null && this.isSectionBlockingRent(choosenDay, intervalKey, isBooked)) {
      this.clearPendingCellAction();
      this.openSectionEventDialog(this.getSectionOccupancyAt(choosenDay, intervalKey));
      return;
    }

    this.clearPendingCellAction();
    this.cancelSelection();
    this.selectedRange = null;

    // Уже есть аренда — сразу карточка брони
    if (isBooked) {
      this.setState({
        intervalData,
        choosenDay,
        chosenPart,
        showDialog: true,
      });
      return;
    }
    
    // Без контекста комплекса — только аренда (как раньше)
    if (!this.props.sportComplexId) {
    this.setState({
      intervalData,
      choosenDay,
      chosenPart,
      showDialog: true,
    });
      return;
    }

    // Свободный слот: выбор действия (аренда / группа / персоналка)
    this.openSlotActionChooser(intervalData, choosenDay, chosenPart, intervalKey);
  }

  clearPendingCellAction = () => {
    if (this._pendingCellAction) {
      clearTimeout(this._pendingCellAction);
      this._pendingCellAction = null;
    }
  };

  openSlotActionChooser = (intervalData, day, part, interval, options = {}) => {
    const selectedRange = options.selectedRange || null;
    this.selectedRange = selectedRange;
    const endIntervalExclusive = selectedRange && selectedRange.endInterval != null
      ? Number(selectedRange.endInterval)
      : null;
    this.setState({
      slotActionChooserOpen: true,
      slotActionContext: {
        intervalData,
        day,
        part,
        interval,
        endIntervalExclusive,
        selectedRange,
      },
      showDialog: false,
    });
  };

  closeSlotActionChooser = () => {
    this.selectedRange = null;
    this.setState({
      slotActionChooserOpen: false,
      slotActionContext: null,
    });
  };

  chooseSlotActionRent = () => {
    const ctx = this.state.slotActionContext;
    if (!ctx) return;
    if (ctx.selectedRange) {
      this.selectedRange = ctx.selectedRange;
    } else {
      this.selectedRange = null;
    }
    this.setState({
      slotActionChooserOpen: false,
      slotActionContext: null,
      intervalData: ctx.intervalData,
      choosenDay: ctx.day,
      chosenPart: ctx.part,
      showDialog: true,
    });
  };

  chooseSlotActionPersonal = () => {
    const ctx = this.state.slotActionContext;
    if (!ctx) return;
    if (!this.props.sportComplexId) {
      this.showSnackbar('Откройте расписание из раздела «Секции»');
      return;
    }
    if (!(this.personalSectionsForCreate() || []).length) {
      this.showSnackbar('Нет секций с персональными тренировками');
      return;
    }
    this.selectedRange = null;
    this.setState({ slotActionChooserOpen: false, slotActionContext: null });
    this.openCreatePersonalFromSlot(
      ctx.day,
      ctx.interval,
      ctx.part,
      ctx.endIntervalExclusive,
    );
  };

  chooseSlotActionGroup = () => {
    const ctx = this.state.slotActionContext;
    if (!ctx) return;
    if (!this.props.sportComplexId) {
      this.showSnackbar('Откройте расписание из раздела «Секции»');
      return;
    }
    if (!(this.state.sectionsCatalog || []).length) {
      this.showSnackbar('Нет секций в комплексе');
      return;
    }
    this.selectedRange = null;
    this.setState({ slotActionChooserOpen: false, slotActionContext: null });
    this.openCreateGroupFromSlot(ctx.day, ctx.interval, ctx.endIntervalExclusive);
  };

  personalSectionsForCreate = () => {
    const list = this.state.sectionsCatalog || [];
    return list.filter((s) => {
      const flag = s.hasPersonalTrainings != null ? s.hasPersonalTrainings : s.HasPersonalTrainings;
      return flag !== false;
    });
  };

  openCreatePersonalFromSlot = (day, interval, part, endIntervalExclusive) => {
    const startLocal = this.intervalToLocalMoment(day, interval);
    const endLocal = endIntervalExclusive != null
      ? this.intervalToLocalMoment(day, endIntervalExclusive)
      : startLocal.clone().add(60, 'minutes');
    if (!endLocal.isAfter(startLocal)) {
      endLocal.add(30, 'minutes');
    }
    const sections = this.personalSectionsForCreate();
    const preferred =
      this.state.sectionFilterId
      && sections.some((s) => String(s.id != null ? s.id : s.Id) === String(this.state.sectionFilterId))
        ? String(this.state.sectionFilterId)
        : (sections[0] && String(sections[0].id != null ? sections[0].id : sections[0].Id)) || '';

    this.setState({
      showDialog: false,
      createPersonalDialogOpen: true,
      createPersonalSaving: false,
      createPersonalError: '',
      createPersonalForm: {
        sectionId: preferred,
        userId: '',
        trainerId: '',
        startDate: startLocal.format('YYYY-MM-DD'),
        startTime: startLocal.format('HH:mm'),
        endDate: endLocal.format('YYYY-MM-DD'),
        endTime: endLocal.format('HH:mm'),
        part: part != null ? part : null,
      },
    });
    this.loadComplexClientsForPicker(preferred || null);
  };

  closeCreatePersonalDialog = () => {
    this.setState({
      createPersonalDialogOpen: false,
      createPersonalSaving: false,
      createPersonalError: '',
    });
  };

  saveCreatePersonalFromSlot = async () => {
    const { createPersonalForm } = this.state;
    const { sportComplexId, playgroundId } = this.props;
    const sectionId = Number(createPersonalForm.sectionId);
    const clientRef = clientRefFromPickerValue(this.state.complexClientOptions, createPersonalForm.userId);
    if (!sectionId) {
      this.setState({ createPersonalError: 'Выберите секцию' });
      return;
    }
    if (!hasPickerClient(clientRef)) {
      this.setState({ createPersonalError: 'Выберите клиента' });
      return;
    }
    const startLocal = moment(
      `${createPersonalForm.startDate} ${createPersonalForm.startTime}`,
      'YYYY-MM-DD HH:mm',
      true,
    );
    const endLocal = moment(
      `${createPersonalForm.endDate} ${createPersonalForm.endTime}`,
      'YYYY-MM-DD HH:mm',
      true,
    );
    if (!startLocal.isValid() || !endLocal.isValid()) {
      this.setState({ createPersonalError: 'Некорректные начало/конец' });
      return;
    }
    if (!endLocal.isAfter(startLocal)) {
      this.setState({ createPersonalError: 'Конец должен быть позже начала' });
      return;
    }

    const durationMinutes = Math.max(1, endLocal.diff(startLocal, 'minutes'));
    const trainerId =
      createPersonalForm.trainerId === '' ? null : Number(createPersonalForm.trainerId);

    this.setState({ createPersonalSaving: true, createPersonalError: '' });
    try {
      await api.createPersonalTraining({
        sectionId,
        ...sectionClientWriteFields(clientRef),
        startAt: startLocal.toISOString(),
        endAt: endLocal.toISOString(),
        durationMinutes,
        playgroundId: playgroundId != null ? Number(playgroundId) : null,
        trainerId,
        sportComplexId: sportComplexId != null ? Number(sportComplexId) : null,
      });
      this.closeCreatePersonalDialog();
      this.showSnackbar(`Персональная создана: ${startLocal.format('DD.MM HH:mm')}–${endLocal.format('HH:mm')}`);
      await this.refreshSectionOverlay();
      this.loadComplexClientsForPicker(sectionId);
    } catch (e) {
      this.setState({
        createPersonalSaving: false,
        createPersonalError:
          formatBookingErrorForUser(e) || (e && e.message) || 'Не удалось создать тренировку',
      });
    }
  };

  openCreateGroupFromSlot = async (day, interval, endIntervalExclusive) => {
    const startLocal = this.intervalToLocalMoment(day, interval);
    const endLocal = endIntervalExclusive != null
      ? this.intervalToLocalMoment(day, endIntervalExclusive)
      : startLocal.clone().add(60, 'minutes');
    if (!endLocal.isAfter(startLocal)) {
      endLocal.add(30, 'minutes');
    }
    const sections = this.state.sectionsCatalog || [];
    const preferred =
      this.state.sectionFilterId
      && sections.some((s) => String(s.id != null ? s.id : s.Id) === String(this.state.sectionFilterId))
        ? String(this.state.sectionFilterId)
        : (sections[0] && String(sections[0].id != null ? sections[0].id : sections[0].Id)) || '';

    this.setState({
      showDialog: false,
      createGroupDialogOpen: true,
      createGroupSaving: false,
      createGroupError: '',
      createGroupGroups: [],
      createGroupForm: {
        sectionId: preferred,
        groupId: this.state.groupFilterId || '',
        trainerId: '',
        startDate: startLocal.format('YYYY-MM-DD'),
        startTime: startLocal.format('HH:mm'),
        endDate: endLocal.format('YYYY-MM-DD'),
        endTime: endLocal.format('HH:mm'),
      },
    }, () => {
      if (preferred) this.loadGroupsForCreateDialog(preferred);
    });
  };

  closeCreateGroupDialog = () => {
    this.setState({
      createGroupDialogOpen: false,
      createGroupSaving: false,
      createGroupError: '',
      createGroupGroups: [],
    });
  };

  loadGroupsForCreateDialog = async (sectionId) => {
    if (!sectionId) {
      this.setState({ createGroupGroups: [], createGroupGroupsLoading: false });
      return;
    }
    this.setState({ createGroupGroupsLoading: true });
    try {
      const groups = normalizeListResponse(await api.getSectionGroups(sectionId));
      this.setState((prev) => {
        const stillOk = String(prev.createGroupForm.sectionId) === String(sectionId);
        let groupId = stillOk ? prev.createGroupForm.groupId : '';
        if (groupId && !groups.some((g) => String(g.id != null ? g.id : g.Id) === String(groupId))) {
          groupId = '';
        }
        if (!groupId && groups.length === 1) {
          groupId = String(groups[0].id != null ? groups[0].id : groups[0].Id);
        }
        const selected = groups.find((g) => String(g.id != null ? g.id : g.Id) === String(groupId));
        const trainerFromGroup = selected && (selected.trainerId != null ? selected.trainerId : selected.TrainerId);
        return {
          createGroupGroups: groups,
          createGroupGroupsLoading: false,
          createGroupForm: {
            ...prev.createGroupForm,
            groupId,
            trainerId: trainerFromGroup != null && trainerFromGroup !== ''
              ? String(trainerFromGroup)
              : prev.createGroupForm.trainerId,
          },
        };
      });
    } catch (e) {
      this.setState({
        createGroupGroups: [],
        createGroupGroupsLoading: false,
        createGroupError: (e && e.message) || 'Не удалось загрузить группы',
      });
    }
  };

  saveCreateGroupFromSlot = async () => {
    const { createGroupForm } = this.state;
    const { playgroundId } = this.props;
    const sectionId = Number(createGroupForm.sectionId);
    const groupId = Number(createGroupForm.groupId);
    if (!sectionId) {
      this.setState({ createGroupError: 'Выберите секцию' });
      return;
    }
    if (!groupId) {
      this.setState({ createGroupError: 'Выберите группу' });
      return;
    }
    const startLocal = moment(
      `${createGroupForm.startDate} ${createGroupForm.startTime}`,
      'YYYY-MM-DD HH:mm',
      true,
    );
    const endLocal = moment(
      `${createGroupForm.endDate} ${createGroupForm.endTime}`,
      'YYYY-MM-DD HH:mm',
      true,
    );
    if (!startLocal.isValid() || !endLocal.isValid()) {
      this.setState({ createGroupError: 'Некорректные начало/конец' });
      return;
    }
    if (!endLocal.isAfter(startLocal)) {
      this.setState({ createGroupError: 'Конец должен быть позже начала' });
      return;
    }

    const durationMinutes = Math.max(1, endLocal.diff(startLocal, 'minutes'));
    const trainerId =
      createGroupForm.trainerId === '' ? null : Number(createGroupForm.trainerId);

    this.setState({ createGroupSaving: true, createGroupError: '' });
    try {
      await api.createSectionOccurrence(sectionId, {
        sectionGroupId: groupId,
        startAt: startLocal.toISOString(),
        endAt: endLocal.toISOString(),
        durationMinutes,
        playgroundId: playgroundId != null ? Number(playgroundId) : null,
        trainerId,
      });
      this.closeCreateGroupDialog();
      this.showSnackbar(`Групповое занятие: ${startLocal.format('DD.MM HH:mm')}–${endLocal.format('HH:mm')}`);
      await this.refreshSectionOverlay();
    } catch (e) {
      this.setState({
        createGroupSaving: false,
        createGroupError:
          formatBookingErrorForUser(e) || (e && e.message) || 'Не удалось создать занятие',
      });
    }
  };

  onCellDoubleClick = (intervalData, choosenDay, chosenPart, isBooked, interval) => {
    // Совпадает с одиночным кликом по свободному слоту (chooser)
    this.clearPendingCellAction();
    this.suppressNextCellClick();
    this.onCellClick(intervalData, choosenDay, chosenPart, isBooked, interval);
  };

  // Загружает данные для недели (пн–вс)
  loadWeekData = async (weekStartMoment, typeId) => {
    try {
      this.setState({ loading: true });
      if (this.state.id) {
        const startDate = weekStartMoment.format('YYYY-MM-DD');
        const endDate = weekStartMoment.clone().add(6, 'days').format('YYYY-MM-DD');
        const timetable = await api.getTimetable(
          this.state.id,
          startDate,
          endDate,
          typeId,
        );
        if (timetable) {
          const schedule = getSchedule(timetable);
          this.setState(prev => ({
            schedule: { ...prev.schedule, ...schedule },
          }));
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки расписания:', error);
      if (error.message && error.message.includes('Unauthorized')) {
        this.showSnackbar('Сессия истекла. Пожалуйста, обновите страницу и войдите заново.');
      } else {
        this.showSnackbar('Ошибка загрузки расписания. Попробуйте обновить страницу.');
      }
    } finally {
      this.setState({ loading: false }, () => this.refreshSectionOverlay());
    }
  };

  loadSectionsCatalog = async () => {
    const cid = this.props.sportComplexId;
    if (cid == null || Number.isNaN(Number(cid))) {
      this.setState({ sectionsCatalog: [], sectionsCatalogError: '' });
      return;
    }
    try {
      const sections = await api.getSections({ complexId: cid });
      this.setState({
        sectionsCatalog: Array.isArray(sections) ? sections : [],
        sectionsCatalogError: '',
      }, () => this.refreshSectionOverlay());
    } catch (e) {
      const msg = (e && e.message) || 'Не удалось загрузить список секций';
      if (process.env.NODE_ENV === 'development') {
        console.warn('Timetable: секции для фильтра', e);
      }
      this.setState({ sectionsCatalog: [], sectionsCatalogError: msg }, () => this.refreshSectionOverlay());
      this.showSnackbar(`Фильтр секций: ${msg}`);
    }
  };

  loadGroupsForFilter = async (sectionId) => {
    if (!sectionId) {
      this.setState({ groupsCatalog: [], groupsCatalogError: '' });
      return;
    }
    try {
      const groups = await api.getSectionGroups(Number(sectionId));
      this.setState({
        groupsCatalog: Array.isArray(groups) ? groups : [],
        groupsCatalogError: '',
      });
    } catch (e) {
      const msg = (e && e.message) || 'Не удалось загрузить группы';
      if (process.env.NODE_ENV === 'development') {
        console.warn('Timetable: группы для фильтра', e);
      }
      this.setState({ groupsCatalog: [], groupsCatalogError: msg });
      this.showSnackbar(`Фильтр групп: ${msg}`);
    }
  };

  /** Парсит StartAt/EndAt occupancy: в БД UTC, JSON часто без Z → иначе слоты уезжают на 3ч. */
  parseOccurrenceMoment = (raw) => parseOccurrenceMomentLib(raw);

  /** Разбивает occurrence на 30-мин слоты для сопоставления с сеткой админки */
  flattenOccurrenceToCells = (occ) => flattenOccurrenceToCellsLib(occ, parseOccurrenceMomentLib);

  refreshSectionOverlay = async () => {
    const { sportComplexId, playgroundId } = this.props;
    const {
      sectionFilterId,
      groupFilterId,
      weekStart,
      timelineViewMode,
      selectedDate,
    } = this.state;
    if (
      sportComplexId == null ||
      Number.isNaN(Number(sportComplexId)) ||
      !playgroundId
    ) {
      this.setState({ sectionOccurrenceMap: {}, sectionOccurrencesLoading: false });
      return;
    }
    let from;
    let to;
    if (timelineViewMode === 'week') {
      from = weekStart.format('YYYY-MM-DD');
      to = weekStart.clone().add(6, 'days').format('YYYY-MM-DD');
    } else {
      from = selectedDate;
      to = selectedDate;
    }
    this.setState({ sectionOccurrencesLoading: true });
    try {
      const params = { from, to };
      if (sectionFilterId) params.sectionId = Number(sectionFilterId);
      if (groupFilterId) params.groupId = Number(groupFilterId);
      const data = await api.getPlaygroundSectionOccupancy(Number(playgroundId), params);
      let list = [];
      if (Array.isArray(data)) list = data;
      else if (data && Array.isArray(data.items)) list = data.items;
      else if (data && Array.isArray(data.results)) list = data.results;
      const map = buildSectionOccurrenceMap(list, Number(playgroundId), (occ) =>
        flattenOccurrenceToCellsLib(occ, parseOccurrenceMomentLib),
      );
      this.setState({ sectionOccurrenceMap: map, sectionOccurrencesLoading: false });
    } catch (e) {
      console.warn('Оверлей секций (occupancy):', e.message);
      this.setState({ sectionOccurrenceMap: {}, sectionOccurrencesLoading: false });
    }
  };

  navigateTimeline = (direction) => {
    const { currentWorkingHoursTypes } = this.props;
    if (this.state.timelineViewMode === 'week') {
      this.navigateWeek(direction);
      return;
    }
    const prevMonth = this.state.currentMonth;
    const next = moment(this.state.selectedDate, 'YYYY-MM-DD').add(direction, 'days');
    const dateStr = next.format('YYYY-MM-DD');
    const newWeekStart = next.clone().startOf('isoWeek');
    const newMonth = next.clone().startOf('month');
    this.setState({
      selectedDate: dateStr,
      weekStart: newWeekStart,
      currentMonth: newMonth,
    });
    this.loadWeekData(newWeekStart, currentWorkingHoursTypes);
    if (!prevMonth.isSame(newMonth, 'month')) {
      this.loadMonthData(newMonth, currentWorkingHoursTypes);
    }
  };

  setTimelineViewMode = (mode) => {
    if (mode === 'week') {
      const ws = moment(this.state.selectedDate, 'YYYY-MM-DD').startOf('isoWeek');
      this.setState({ timelineViewMode: mode, weekStart: ws }, () => {
        this.loadWeekData(ws, this.props.currentWorkingHoursTypes);
        this.loadMonthData(this.state.currentMonth, this.props.currentWorkingHoursTypes);
        this.refreshSectionOverlay();
      });
    } else {
      this.setState({ timelineViewMode: mode }, () => this.refreshSectionOverlay());
    }
  };

  handleSectionFilterChange = (event) => {
    const sectionFilterId = event.target.value;
    this.setState({ sectionFilterId, groupFilterId: '' }, () => {
      this.loadGroupsForFilter(sectionFilterId);
      this.refreshSectionOverlay();
    });
  };

  handleGroupFilterChange = (event) => {
    const groupFilterId = event.target.value;
    this.setState({ groupFilterId }, () => this.refreshSectionOverlay());
  };

  // Загружает данные для месяца (для календаря — точки бронирований)
  loadMonthData = async (month, typeId) => {
    try {
      if (this.state.id) {
        const startDate = month.startOf('month').format('YYYY-MM-DD');
        const endDate = month.endOf('month').format('YYYY-MM-DD');
        const timetable = await api.getTimetable(
          this.state.id,
          startDate,
          endDate,
          typeId,
        );
        if (timetable) {
          const schedule = getSchedule(timetable);
          this.setState(prev => ({
            schedule: { ...prev.schedule, ...schedule },
          }));
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки расписания календаря:', error);
    }
  }

  // Загружает данные для выбранного дня (для обратной совместимости)
  getTimetable = async (date, typeId) => {
    const weekStartMoment = moment(date, 'YYYY-MM-DD').startOf('isoWeek');
    await this.loadWeekData(weekStartMoment, typeId);
  }

  handleExportExcel = async () => {
    const { playgroundId, currentWorkingHoursTypes } = this.props;
    const { currentMonth } = this.state;
    
    const startDate = currentMonth.startOf('month').format('YYYY-MM-DD');
    const endDate = currentMonth.endOf('month').format('YYYY-MM-DD');
    
    const typeId = currentWorkingHoursTypes === 'full' ? 0 : 
                   currentWorkingHoursTypes === 'half' ? 1 : 2;
    
    try {
      const response = await fetch(
        `${API_URL}/schedule/${playgroundId}/export?from=${startDate}&to=${endDate}&type=${typeId}`,
        {
          headers: api.getDefaultHeaders(),
        }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Расписание_${startDate}_${endDate}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        this.showSnackbar('Расписание успешно экспортировано');
      } else {
        let errorMessage = `Ошибка ${response.status}: ${response.statusText}`;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const error = await response.json();
            errorMessage = error.detail || error.title || errorMessage;
          } else {
            const text = await response.text();
            if (text) errorMessage = text;
          }
        } catch (e) {
          console.error('Ошибка при парсинге ответа:', e);
        }
        this.showSnackbar(`Ошибка при экспорте: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Ошибка экспорта:', error);
      this.showSnackbar('Ошибка при экспорте расписания');
    }
  };

  handleDownloadTemplate = async () => {
    const { playgroundId, currentWorkingHoursTypes } = this.props;
    const typeId = currentWorkingHoursTypes === 'full' ? 0 : 
                   currentWorkingHoursTypes === 'half' ? 1 : 2;
    
    try {
      const response = await fetch(
        `${API_URL}/schedule/${playgroundId}/template?type=${typeId}`,
        {
          headers: api.getDefaultHeaders(),
        }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Шаблон_импорта.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        this.showSnackbar('Шаблон успешно скачан');
      } else {
        let errorMessage = `Ошибка ${response.status}: ${response.statusText}`;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const error = await response.json();
            errorMessage = error.detail || error.title || errorMessage;
          } else {
            const text = await response.text();
            if (text) errorMessage = text;
          }
        } catch (e) {
          console.error('Ошибка при парсинге ответа:', e);
        }
        this.showSnackbar(`Ошибка при скачивании шаблона: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Ошибка скачивания шаблона:', error);
      this.showSnackbar('Ошибка при скачивании шаблона');
    }
  };

  handleImportExcel = async (event) => {
    // Сохраняем ссылку на input до асинхронных операций
    const inputElement = event.target;
    const file = inputElement.files[0];
    if (!file) return;
    
    const { playgroundId, currentWorkingHoursTypes } = this.props;
    const typeId = currentWorkingHoursTypes === 'full' ? 0 : 
                   currentWorkingHoursTypes === 'half' ? 1 : 2;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(
        `${API_URL}/schedule/${playgroundId}/import?type=${typeId}`,
        {
          method: 'POST',
          headers: {
            ...api.getDefaultHeaders(),
            // Не устанавливаем Content-Type, браузер установит автоматически с boundary
          },
          body: formData,
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        const message = `Импортировано бронирований: ${result.createdCount || result.CreatedCount || 0}. Ошибок: ${result.errors?.length || result.Errors?.length || 0}`;
        this.showSnackbar(message);
        if ((result.errors && result.errors.length > 0) || (result.Errors && result.Errors.length > 0)) {
          const errors = result.errors || result.Errors || [];
          console.warn('Ошибки импорта:', errors);
          if (errors.length > 0) {
            alert(`Ошибки импорта:\n${errors.slice(0, 10).join('\n')}${errors.length > 10 ? `\n... и еще ${errors.length - 10} ошибок` : ''}`);
          }
        }
        // Перезагружаем расписание
        await this.loadWeekData(this.state.weekStart, currentWorkingHoursTypes);
        await this.loadMonthData(this.state.currentMonth, currentWorkingHoursTypes);
      } else {
        let errorMessage = `Ошибка ${response.status}: ${response.statusText}`;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const error = await response.json();
            errorMessage = error.detail || error.title || errorMessage;
          } else {
            const text = await response.text();
            if (text) errorMessage = text;
          }
        } catch (e) {
          console.error('Ошибка при парсинге ответа:', e);
        }
        this.showSnackbar(`Ошибка при импорте: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Ошибка импорта:', error);
      this.showSnackbar(`Ошибка при импорте расписания: ${error.message || 'Неизвестная ошибка'}`);
    } finally {
      // Сбрасываем input в finally, чтобы это произошло в любом случае
      if (inputElement) {
        inputElement.value = '';
      }
    }
  };

  showSnackbar = (message) => {
    this.setState({
      isSnackOpen: true,
      snackMessage: message,
    });
  };

  handleCloseSnackbar = () => {
    this.setState({
      isSnackOpen: false,
      snackMessage: '',
    });
  };

  // eslint-disable-next-line react/sort-comp
  deleteBooking = async (booking) => {
    const { currentWorkingHoursTypes } = this.props;
    if (booking.state === 'Paid') {
      const isCanceled = await api.paidCancel(booking);

      if (!isCanceled) {
        this.setState({
          isSnackOpen: true,
          snackMessage: 'Бронирование не отменено (не удалось отменить оплату)',
        });
        this.loadWeekData(this.state.weekStart, currentWorkingHoursTypes);
      this.loadMonthData(this.state.currentMonth, currentWorkingHoursTypes);
        return;
      }
    }
    // оплата успешно отменена
    const isDeleted = await api.deleteBooking(booking.id);

    if (isDeleted) {
      this.setState({
        isSnackOpen: true,
        showDialog: false,
        snackMessage: 'Бронирование успешно отменено. Если бронирование было оплачено, то деньги будут возвращены',
      });
      this.loadWeekData(this.state.weekStart, currentWorkingHoursTypes);
      this.loadMonthData(this.state.currentMonth, currentWorkingHoursTypes);
    } else {
      this.setState({
        isSnackOpen: true,
        snackMessage: 'Оплата была отменена, но бронирование не удалено. Попробуйте еще раз',
      });
      this.loadWeekData(this.state.weekStart, currentWorkingHoursTypes);
      this.loadMonthData(this.state.currentMonth, currentWorkingHoursTypes);
    }
  }

  // Бронирование администратором
  bookInterval = async (booking) => {
    try {
      this.setState({
        loading: true,
      });
      const {
        startInterval,
        endInterval,
        choosenDay,
        chosenPart,
        user,
        price,
        sport,
        details,
        userId,
      } = booking;
      const { playgroundId, currentWorkingHoursTypes } = this.props;
      
      // Преобразуем type из строки в число (0 = Full, 1 = Half, 2 = Third)
      let typeValue = 0;
      if (currentWorkingHoursTypes === 'half' || currentWorkingHoursTypes === '1' || currentWorkingHoursTypes === 1) {
        typeValue = 1;
      } else if (currentWorkingHoursTypes === 'third' || currentWorkingHoursTypes === '2' || currentWorkingHoursTypes === 2) {
        typeValue = 2;
      }
      
      // Преобразуем part из строки в число согласно PlaygroundPart enum
      // All = 0, Left = 1 << 0 = 1, Right = 1 << 1 = 2, Middle = 1 << 2 = 4
      let partValue = 0; // All по умолчанию
      if (chosenPart === 'left' || chosenPart === 'Left') {
        partValue = 1; // Left = 1 << 0
      } else if (chosenPart === 'right' || chosenPart === 'Right') {
        partValue = 2; // Right = 1 << 1
      } else if (chosenPart === 'middle' || chosenPart === 'Middle') {
        partValue = 4; // Middle = 1 << 2
      }
      
      // Преобразуем sportType из строки в числовой флаг
      let sportTypeValue = null;
      if (sport) {
        const sportFlags = sportKeysToFlagsArray([sport]);
        if (sportFlags && sportFlags.length > 0) {
          sportTypeValue = sportFlags[0];
        }
      }
      
      const intervals = times(endInterval - startInterval, index => ({
        interval: startInterval + index,
        part: partValue,
        type: typeValue,
      }));

      // Формируем объект details правильно - только если есть хотя бы одно непустое поле
      let bookingDetails = null;
      if (details) {
        const hasUser = details.user && details.user.trim() !== '';
        const hasPhone = details.phone && details.phone.trim() !== '';
        const hasComment = details.comment && details.comment.trim() !== '';
        
        if (hasUser || hasPhone || hasComment) {
          bookingDetails = {};
          if (hasUser) bookingDetails.user = details.user.trim();
          if (hasPhone) bookingDetails.phone = details.phone.trim();
          if (hasComment) bookingDetails.comment = details.comment.trim();
        }
      }

      const newBooking = {
        date: choosenDay,
        playgroundId,
        intervals,
        price: price || 0,
        sportType: sportTypeValue,
        type: typeValue,
        ...(user ? { user } : {}), // user только если передан (для пользовательских бронирований)
        ...(userId ? { userId } : {}),
        ...(bookingDetails ? { details: bookingDetails } : {}), // details только если есть данные
        ...(booking.rentalItems && booking.rentalItems.length > 0 ? { rentalItems: booking.rentalItems } : {}), // услуги/инвентарь
      };

      // Валидация обязательных полей
      if (!choosenDay || !playgroundId || !sport || !intervals || intervals.length === 0) {
        throw new Error('Не все обязательные поля заполнены');
      }

      console.log('🔍 Отправка бронирования:', JSON.stringify(newBooking, null, 2));
      await api.createBooking(newBooking);
      this.loadWeekData(this.state.weekStart, currentWorkingHoursTypes);
      this.loadMonthData(this.state.currentMonth, currentWorkingHoursTypes);
      this.setState({
        showDialog: false,
      });
    } catch (err) {
      this.setState({
        isSnackOpen: true,
        snackMessage: formatBookingErrorForUser(err),
      });
    } finally {
      this.setState({
        loading: false,
      });
    }
  }

  // Проверяет, есть ли бронирования для даты
  hasBookingsForDate = (date, schedule) => {
    if (!schedule || !schedule[date] || schedule[date].length === 0) return false;
    const daySchedule = schedule[date];
    
    return daySchedule.some(intervalData => 
      intervalData && 
      intervalData.values && 
      intervalData.values.some(value => 
        value.bookings && value.bookings.length > 0
      )
    );
  }

  // Выбор даты в календаре — переключаем на неделю, содержащую этот день
  selectDate = (date) => {
    const dateMoment = moment(date, 'YYYY-MM-DD');
    const newWeekStart = dateMoment.clone().startOf('isoWeek');
    this.setState({
      selectedDate: date,
      currentMonth: dateMoment.clone().startOf('month'),
      weekStart: newWeekStart,
      calendarAnchorEl: null,
    });
    this.loadWeekData(newWeekStart, this.props.currentWorkingHoursTypes);
    if (!this.state.currentMonth.isSame(dateMoment.clone().startOf('month'))) {
      this.loadMonthData(dateMoment.clone().startOf('month'), this.props.currentWorkingHoursTypes);
    }
  }

  goToToday = () => {
    this.selectDate(moment().format('YYYY-MM-DD'));
  }

  openCalendar = (event) => {
    const { timelineViewMode, selectedDate, weekStart } = this.state;
    const focus = timelineViewMode === 'day'
      ? moment(selectedDate, 'YYYY-MM-DD')
      : weekStart;
    const month = focus.clone().startOf('month');
    this.setState({
      calendarAnchorEl: event.currentTarget,
      currentMonth: month,
    });
    this.loadMonthData(month, this.props.currentWorkingHoursTypes);
  }

  closeCalendar = () => {
    this.setState({ calendarAnchorEl: null });
  }

  // Навигация по неделям стрелками
  navigateWeek = (direction) => {
    const newWeekStart = this.state.weekStart.clone().add(direction, 'weeks');
    this.setState({
      weekStart: newWeekStart,
      selectedDate: newWeekStart.format('YYYY-MM-DD'),
      currentMonth: newWeekStart.clone().startOf('month'),
    });
    this.loadWeekData(newWeekStart, this.props.currentWorkingHoursTypes);
    if (!this.state.currentMonth.isSame(newWeekStart.clone().startOf('month'))) {
      this.loadMonthData(newWeekStart.clone().startOf('month'), this.props.currentWorkingHoursTypes);
    }
  }

  // Навигация по месяцам в календаре
  navigateMonth = (direction) => {
    const newMonth = moment(this.state.currentMonth).add(direction, 'months');
    this.setState({ currentMonth: newMonth });
    this.loadMonthData(newMonth, this.props.currentWorkingHoursTypes);
    // Если текущая неделя попадает в новый месяц, подгружаем неделю
    const { weekStart } = this.state;
    if (weekStart.isSame(newMonth, 'month') || weekStart.clone().add(6, 'days').isSame(newMonth, 'month')) {
      this.loadWeekData(weekStart, this.props.currentWorkingHoursTypes);
    }
  }

  renderCalendar = () => {
    const { classes } = this.props;
    const {
      schedule, selectedDate, currentMonth, weekStart, timelineViewMode,
    } = this.state;

    const monthStart = currentMonth.clone().startOf('month');
    const daysInMonth = monthStart.daysInMonth();
    const firstDayOfWeek = monthStart.day();
    const offset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    const weekDays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
    const weekFrom = weekStart.format('YYYY-MM-DD');
    const weekTo = weekStart.clone().add(6, 'days').format('YYYY-MM-DD');
    const todayStr = moment().format('YYYY-MM-DD');
    
    return (
      <div className={classes.calendarPanel}>
        <div className={classes.calendarMonthRow}>
          <IconButton 
            size="small" 
            onClick={() => this.navigateMonth(-1)}
            aria-label="Предыдущий месяц"
          >
            <span aria-hidden="true">‹</span>
          </IconButton>
          <Typography className={classes.calendarMonthLabel}>
            {monthStart.format('MMMM YYYY')}
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => this.navigateMonth(1)}
            aria-label="Следующий месяц"
          >
            <span aria-hidden="true">›</span>
          </IconButton>
        </div>
        <div className={classes.calendarWeekdays}>
          {weekDays.map((day) => (
            <div key={day} className={classes.calendarWeekday}>{day}</div>
          ))}
        </div>
        <div className={classes.calendarGrid}>
          {Array.from({ length: offset }).map((_, index) => (
            <div key={`empty-${index}`} className={classes.calendarCell} />
          ))}
          {Array.from({ length: daysInMonth }).map((_, index) => {
            const day = index + 1;
            const date = monthStart.clone().date(day).format('YYYY-MM-DD');
            const dateMoment = moment(date, 'YYYY-MM-DD');
            const isPast = dateMoment.isBefore(moment().startOf('day'));
            const isSelected = selectedDate === date;
            const isToday = todayStr === date;
            const inWeek = timelineViewMode === 'week' && date >= weekFrom && date <= weekTo;
            const hasBookings = this.hasBookingsForDate(date, schedule);
            return (
              <div key={day} className={classes.calendarCell}>
                <button
                  type="button"
                onClick={() => this.selectDate(date)}
                  className={classNames(classes.calendarDay, {
                    [classes.calendarDaySelected]: isSelected,
                    [classes.calendarDayToday]: isToday && !isSelected,
                    [classes.calendarDayInWeek]: inWeek && !isSelected,
                    [classes.calendarDayPast]: isPast && !isSelected,
                  })}
                  aria-label={dateMoment.format('D MMMM YYYY')}
                  aria-current={isToday ? 'date' : undefined}
                  aria-pressed={isSelected}
                >
                  {day}
                  {hasBookings ? (
                    <span
                      className={classNames(classes.calendarDot, {
                        [classes.calendarDotOnSelected]: isSelected,
                      })}
                    />
                  ) : null}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  getPeriodLabel = () => {
    const { timelineViewMode, weekStart, selectedDate } = this.state;
    if (timelineViewMode === 'week') {
      return `${weekStart.format('D MMM')} – ${weekStart.clone().add(6, 'days').format('D MMM YYYY')}`;
    }
    return moment(selectedDate, 'YYYY-MM-DD').format('D MMMM YYYY, dd');
  }

  renderTimelinePeriodHeader = () => {
    const { classes } = this.props;
    const { calendarAnchorEl, timelineViewMode, weekStart, selectedDate } = this.state;
    const calendarOpen = Boolean(calendarAnchorEl);
    const viewingToday = timelineViewMode === 'week'
      ? weekStart.format('YYYY-MM-DD') === moment().startOf('isoWeek').format('YYYY-MM-DD')
      : selectedDate === moment().format('YYYY-MM-DD');

    return (
      <div className={classes.periodBar}>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          onClick={this.goToToday}
          disabled={viewingToday}
          className={classes.todayButton}
        >
          Сегодня
        </Button>
        <div className={classes.periodNav}>
          <IconButton
            onClick={() => this.navigateTimeline(-1)}
            color="primary"
            className={classes.periodArrow}
            aria-label={timelineViewMode === 'week' ? 'Предыдущая неделя' : 'Предыдущий день'}
          >
            <span aria-hidden="true">‹</span>
          </IconButton>
          <Tooltip title="Выбрать дату" placement="bottom">
            <button
              type="button"
              className={classes.periodButton}
              onClick={this.openCalendar}
              aria-haspopup="dialog"
              aria-expanded={calendarOpen}
              aria-label={`${this.getPeriodLabel()}, открыть календарь`}
            >
              <span className={classes.periodLabel}>{this.getPeriodLabel()}</span>
              <span className={classes.periodCaret} aria-hidden="true">▾</span>
            </button>
          </Tooltip>
          <IconButton
            onClick={() => this.navigateTimeline(1)}
            color="primary"
            className={classes.periodArrow}
            aria-label={timelineViewMode === 'week' ? 'Следующая неделя' : 'Следующий день'}
          >
            <span aria-hidden="true">›</span>
          </IconButton>
        </div>
        <Popover
          open={calendarOpen}
          anchorEl={calendarAnchorEl}
          onClose={this.closeCalendar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          PaperProps={{ className: classes.calendarPopoverPaper, elevation: 8 }}
        >
          {this.renderCalendar()}
        </Popover>
      </div>
    );
  }

  // Рендерит детали бронирования для ячейки
  renderBookingDetails = (item, wrap = false, textColor = '#ffffff', timeLabel = '') => {
    if (!item.bookings || item.bookings.length === 0) {
      return ' '; // Возвращаем пробел вместо null, чтобы Button всегда имел children
    }
    
    const booking = item.bookings[0]; // Берем первое бронирование
    
    // Преобразуем числовой флаг в строковый ключ, затем в название
    let sportName = '';
    if (booking.sportType) {
      const sportKey = sportFlagsToKeys[booking.sportType];
      sportName = sportKey ? sportsEnum[sportKey] : '';
    }
    
    const userName = booking.details?.user || booking.user?.name || '';
    const title = sportName || userName || 'Аренда';
    return this.renderChipLabel(title, timeLabel || '', textColor, wrap);
  }

  renderSectionSlotLabel = (occOverlay, wrap = false, textColor = '#ffffff', timeLabel = '') => {
    const text = this.shortSectionLabel(occOverlay);
    if (!text) return null;
    return this.renderChipLabel(text, timeLabel, textColor, wrap);
  };

  renderCellInnerContent = (item, occOverlay, options = {}) => {
    const wrap = !!options.wrap;
    const textColor = options.textColor || '#ffffff';
    const timeLabel = options.timeLabel || this.formatChipTime(occOverlay, options.interval);
    if (occOverlay && occOverlay.label) {
      const sectionLabel = this.renderSectionSlotLabel(occOverlay, wrap, textColor, timeLabel);
      if (sectionLabel) return sectionLabel;
    }
    return this.renderBookingDetails(item, wrap, textColor, timeLabel);
  };

  // Рендерит интервалы для одного дня, сгруппированные по часам (вертикально)
  renderDayIntervals = (day, hourArray, classes) => {
    // Группируем интервалы по часам: [0,1] для 00:00, [2,3] для 01:00, [4,5] для 02:00, ...
    const hourGroups = [];
    for (let i = 0; i < 48; i += 2) {
      const hour = Math.floor(i / 2);
      hourGroups.push({
        hour,
        intervals: [i, i + 1],
      });
    }

    return hourGroups.map((group, groupIndex) => {
      // Получаем время для каждого интервала
      const hourSlot = intervalSlotTimeRange(group.intervals[0]);
      const halfSlot = intervalSlotTimeRange(group.intervals[1]);
      
      return (
        <div key={groupIndex} className={classes.hourGroup} style={{ display: 'flex', marginBottom: 8 }}>
          <div style={{ width: 44, paddingRight: 8, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between', minHeight: 44 }}>
            <Typography type="body2" style={{ fontWeight: 'bold', fontSize: 12 }} title={hourSlot.title}>
              {hourSlot.start}
            </Typography>
            <Typography type="caption" style={{ fontSize: 10, color: '#888' }} title={halfSlot.title}>
              {halfSlot.start}
            </Typography>
            <div style={{ height: 0 }} />
          </div>
          
          {/* Интервалы справа, расположенные вертикально */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {group.intervals.map((interval) => {
              const intervalData = hourArray[interval];
              if (intervalData) {
                // Сортируем части площадки: левая, средняя, правая
                const sortedValues = intervalData.values.sort((a, b) =>
                  sortWorkingHourPartValue[a.part] - sortWorkingHourPartValue[b.part]);
                
                return (
                  <div key={interval} style={{ width: '100%', display: 'flex', gap: 4 }}>
                    {/* eslint-disable-next-line */}
                    { sortedValues.map((item, valIndex) => {
                      const { hint, isBooked } = getTipData(item);
                      const occOverlay = this.getSectionOccupancyAt(day, interval);
                      const occLabel = occOverlay && occOverlay.label;
                      const occKind = (occOverlay && occOverlay.kind) || 'section_occurrence';
                      const sectionBlocksRent = this.isSectionBlockingRent(day, interval, isBooked);
                      const hintFull = occLabel
                        ? `${hint} • ${occKind === 'personal_training' ? 'Персональная' : 'Секция'}: ${occLabel}${sectionBlocksRent ? ' (аренда недоступна)' : ''}`
                        : hint;
                      const isDisabled = !intervalData;
                      const isSelected = this.isCellSelected(interval, item.part, day);
                      const { sectionDrag, sectionDropTarget } = this.state;
                      const isDropTarget = !!(
                        sectionDrag
                        && sectionDropTarget
                        && sectionDropTarget.day === day
                        && Number(sectionDropTarget.interval) === Number(interval)
                      );
                      const isDragSource = !!(
                        sectionDrag
                        && sectionDrag.originDay === day
                        && Number(sectionDrag.originInterval) === Number(interval)
                      );
                      
                      // Прошедшие свободные слоты — нельзя бронировать; занятия/аренда — кликабельны
                      const isPast = this.isPastDay(day);
                      const isPastDisabled = this.isPastCellDisabled(day, isBooked, !!occOverlay);
                      const visual = this.resolveCellVisualStyle(item, occOverlay, { isPast });
                      const buttonContent = this.renderCellInnerContent(item, occOverlay, {
                        wrap: true,
                        interval,
                        textColor: visual.color,
                      });
                      
                      // Определяем стили для выделенной ячейки
                      const cellStyle = {
                        flex: 1,
                        position: 'relative',
                        minHeight: 28,
                        backgroundColor: visual.backgroundColor,
                        color: visual.color,
                        borderRadius: visual.borderRadius,
                        margin: visual.margin,
                        boxShadow: visual.boxShadow,
                        ...(occLabel ? {
                          cursor: occOverlay && occOverlay.event
                            ? this.sectionCellCursor(occOverlay, day, interval)
                            : (sectionBlocksRent ? 'not-allowed' : undefined),
                        } : {}),
                        ...(isDragSource ? { opacity: 0.55, cursor: 'grabbing' } : {}),
                        ...(isDropTarget ? {
                          outline: `2px dashed ${TIMETABLE_SELECT_BORDER}`,
                          outlineOffset: -2,
                          backgroundColor: TIMETABLE_SELECT,
                        } : {}),
                        ...(isSelected ? {
                          backgroundColor: TIMETABLE_SELECT,
                          outline: `2px solid ${TIMETABLE_SELECT_BORDER}`,
                          outlineOffset: -2,
                          boxSizing: 'border-box',
                        } : {}),
                      };
                      
                      // Если кнопка disabled, оборачиваем в div для Tooltip
                      const buttonElement = (
                        <Button
                          key={`${item.$id}${valIndex}`} // eslint-disable-line react/no-array-index-key
                          disabled={isDisabled || isPastDisabled}
                          variant="text"
                          disableRipple
                          className={classes.btnPeriod}
                          style={{
                            ...cellStyle,
                            ...(isPastDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                          }}
                          onClick={() => !isDisabled && !isPastDisabled && this.onCellClick(
                            intervalData,
                            day,
                            item.part,
                            isBooked,
                            interval,
                          )}
                          onDoubleClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!isDisabled && !isPastDisabled) {
                              this.onCellDoubleClick(
                                intervalData,
                                day,
                                item.part,
                                isBooked,
                                interval,
                              );
                            }
                          }}
                          onMouseDown={(e) => !isDisabled && !isPastDisabled && this.onCellMouseDown(
                            e,
                            intervalData,
                            day,
                            item.part,
                            interval,
                            isBooked,
                          )}
                          onMouseEnter={(e) => !isDisabled && !isPastDisabled && this.onCellMouseEnter(
                            e,
                            intervalData,
                            day,
                            item.part,
                            interval,
                          )}
                          onMouseUp={(e) => !isDisabled && !isPastDisabled && this.onCellMouseUp(
                            e,
                            intervalData,
                            day,
                            item.part,
                            isBooked,
                          )}
                          data-timetable-cell="true"
                          data-day={day}
                          data-interval={interval}
                          data-rowspan="1"
                        >
                          {buttonContent}
                        </Button>
                      );
                      
                      return (
                        <Tooltip
                          id="tooltip-top"
                          key={`${interval}${valIndex}`} // eslint-disable-line react/no-array-index-key
                          title={hintFull}
                          placement="top"
                          className={classes.tooltip}
                        >
                          {(isDisabled || isPastDisabled) ? (
                            <div style={{ flex: 1 }}>
                              {buttonElement}
                            </div>
                          ) : (
                            buttonElement
                          )}
                        </Tooltip>);
                      })}
                  </div>
                );
              }
              // Интервал вне рабочего графика
              return (
                <Tooltip
                  key={interval} // eslint-disable-line react/no-array-index-key
                  id="tooltip-top"
                  title="Вне рабочего графика"
                  placement="top"
                >
                  <div style={{ flex: 1 }}>
                    <Button
                      disabled={true}
                      variant="text"
                      disableRipple
                      className={classes.btnPeriod}
                      style={{
                        width: '100%',
                        minHeight: 28,
                        backgroundColor: TIMETABLE_CHIP.closed.bg,
                        color: TIMETABLE_CHIP.closed.fg,
                        borderRadius: 0,
                        margin: 0,
                        boxShadow: 'none',
                      }}
                    >
                      {' '}
                    </Button>
                  </div>
                </Tooltip>);
            })}
          </div>
        </div>
      );
    });
  }

  // Рендерит одну строку интервала (части площадки) для дня — используется в недельной сетке
  renderOneIntervalRow = (day, interval, hourArray, classes, spanInfo = {}) => {
    const rowspan = spanInfo.rowspan || 1;
    const mergeKey = spanInfo.mergeKey || null;
    const spanMeta = { rowspan, startInterval: interval, mergeKey };
    const intervalData = hourArray && hourArray[interval];
    if (intervalData) {
      const sortedValues = intervalData.values.sort((a, b) =>
        sortWorkingHourPartValue[a.part] - sortWorkingHourPartValue[b.part]);
      return (
        <div key={interval} className={classes.sheetSlotRow}>
          {sortedValues.map((item, valIndex) => {
            const { hint, isBooked } = getTipData(item);
            const occOverlay = this.getSectionOccupancyAt(day, interval);
            const occLabel = occOverlay && occOverlay.label;
            const occKind = (occOverlay && occOverlay.kind) || 'section_occurrence';
            const sectionBlocksRent = this.isSectionBlockingRent(day, interval, isBooked);
            const booking = item.bookings && item.bookings[0];
            const canDragOcc = !!(occOverlay && occOverlay.event && !isBooked);
            const canDragBooking = !!(isBooked && booking && (booking.id != null || booking.Id != null) && !occLabel);
            const canDragBlock = canDragOcc || canDragBooking;
            const hintFull = occLabel
              ? `${hint} • ${occKind === 'personal_training' ? 'Персональная' : 'Секция'}: ${occLabel}${sectionBlocksRent ? ' (аренда недоступна)' : ''}${rowspan > 1 ? ` • ${rowspan * 30} мин` : ''}`
              : (isBooked && rowspan > 1 ? `${hint} • ${rowspan * 30} мин` : hint);
            const isDisabled = !intervalData;
            const isSelected = this.isCellSelected(interval, item.part, day);
            const isPast = this.isPastDay(day);
            const isPastDisabled = this.isPastCellDisabled(day, isBooked, !!occOverlay);
            let isDropTarget = false;
            for (let off = 0; off < rowspan; off += 1) {
              if (this.isIntervalInDragPreview(day, interval + off)) {
                isDropTarget = true;
                break;
              }
            }
            const isDragSource = this.isDragSourceBlock(day, interval);
            const resizePreview = this.getMergedResizePreview(day, interval, rowspan);
            if (resizePreview && resizePreview.active) {
              isDropTarget = false;
            }
            const visual = this.resolveCellVisualStyle(item, occOverlay, { isPast });
            const buttonContent = this.renderCellInnerContent(item, occOverlay, {
              wrap: rowspan > 1,
              interval,
              textColor: visual.color,
            });
            const cellStyle = {
              backgroundColor: visual.backgroundColor,
              color: visual.color,
              borderRadius: visual.borderRadius,
              margin: visual.margin,
              boxShadow: visual.boxShadow,
              boxSizing: 'border-box',
              ...(visual.margin
                ? {
                  height: `calc(100% - ${Number(visual.margin) * 2}px)`,
                  width: `calc(100% - ${Number(visual.margin) * 2}px)`,
                  alignSelf: 'center',
                }
                : {}),
              ...(occLabel ? {
                cursor: occOverlay && occOverlay.event
                  ? this.sectionCellCursor(occOverlay, day, interval, rowspan)
                  : (sectionBlocksRent ? 'not-allowed' : undefined),
              } : {}),
              ...(canDragBooking ? { cursor: 'grab' } : {}),
              ...(isDragSource && !(resizePreview && resizePreview.active) ? { opacity: 0.55, cursor: 'grabbing' } : {}),
              ...(isDropTarget ? {
                boxShadow: `inset 0 0 0 2px ${TIMETABLE_SELECT_BORDER}`,
                backgroundColor: TIMETABLE_SELECT,
              } : {}),
              ...(isSelected ? {
                backgroundColor: TIMETABLE_SELECT,
                boxShadow: `inset 0 0 0 2px ${TIMETABLE_SELECT_BORDER}`,
              } : {}),
            };
            const buttonElement = (
              <Button
                key={`${day}-${interval}-${valIndex}`}
                disabled={isDisabled || isPastDisabled}
                variant="text"
                disableRipple
                className={classes.btnPeriod}
                style={{ ...cellStyle, ...(isPastDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                onClick={() => !isDisabled && !isPastDisabled && this.onCellClick(intervalData, day, item.part, isBooked, interval)}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isDisabled && !isPastDisabled) {
                    this.onCellDoubleClick(intervalData, day, item.part, isBooked, interval);
                  }
                }}
                onMouseDown={(e) => !isDisabled && !isPastDisabled && this.onCellMouseDown(e, intervalData, day, item.part, interval, isBooked, spanMeta)}
                onMouseEnter={(e) => !isDisabled && !isPastDisabled && this.onCellMouseEnter(e, intervalData, day, item.part, interval, spanMeta)}
                onMouseUp={(e) => !isDisabled && !isPastDisabled && this.onCellMouseUp(e, intervalData, day, item.part, isBooked)}
                data-timetable-cell="true"
                data-day={day}
                data-interval={interval}
                data-rowspan={rowspan}
              >
                {buttonContent}
              </Button>
            );
            const startHandleDrag = (handle) => (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (canDragOcc) {
                this.startSectionDrag(e, day, interval, occOverlay, { rowspan, handle });
                return;
              }
              if (canDragBooking) {
                this.startBookingDrag(e, day, interval, booking, item.part, intervalData, {
                  rowspan,
                  handle,
                });
              }
            };
            const previewBlockStyle = (resizePreview && resizePreview.active)
              ? {
                position: 'absolute',
                left: 0,
                right: 0,
                height: `${Math.max(0.12, resizePreview.heightRatio) * 100}%`,
                top: resizePreview.align === 'bottom' ? 'auto' : 0,
                bottom: resizePreview.align === 'bottom' ? 0 : 'auto',
                zIndex: 2,
                boxShadow: `inset 0 0 0 2px ${TIMETABLE_SELECT_BORDER}`,
              }
              : undefined;
            const withHandles = canDragBlock ? (
              <div className={classes.sheetBlockWrap}>
                {resizePreview && resizePreview.active ? <div className={classes.sheetResizeVacate} /> : null}
                <div
                  className={classes.sheetBlock}
                  style={previewBlockStyle}
                  data-timetable-cell="true"
                  data-day={day}
                  data-interval={interval}
                  data-rowspan={rowspan}
                >
                <div
                  className={classNames(classes.sheetResizeEdge, classes.sheetResizeEdgeTop)}
                  data-resize="start"
                  onMouseDown={startHandleDrag('start')}
                />
                {buttonElement}
                <div
                  className={classNames(classes.sheetResizeEdge, classes.sheetResizeEdgeBottom)}
                  data-resize="end"
                  onMouseDown={startHandleDrag('end')}
                />
                <div
                  className={classes.sheetFillHandle}
                  data-resize="end"
                  title="Изменить длительность"
                  onMouseDown={startHandleDrag('end')}
                />
              </div>
              </div>
            ) : buttonElement;
            return (
              <Tooltip key={`${interval}-${valIndex}`} title={hintFull} placement="top" className={classes.tooltip}>
                {(isDisabled || isPastDisabled) ? <div className={classes.sheetSlotFill}>{withHandles}</div> : withHandles}
              </Tooltip>
            );
          })}
        </div>
      );
    }
    return (
      <div className={classes.sheetSlotRow}>
        <Tooltip key={interval} title="Вне рабочего графика" placement="top" className={classes.tooltip}>
          <div className={classes.sheetSlotFill}>
            <Button
              disabled
              variant="text"
              disableRipple
              className={classes.btnPeriod}
              style={{
                backgroundColor: TIMETABLE_CHIP.closed.bg,
                color: TIMETABLE_CHIP.closed.fg,
                borderRadius: 0,
                margin: 0,
                boxShadow: 'none',
              }}
            >
              {' '}
            </Button>
        </div>
      </Tooltip>
      </div>
    );
  };

  renderTimelineLegend = () => {
    const { classes } = this.props;
    const { legendHelpAnchorEl } = this.state;
    const open = Boolean(legendHelpAnchorEl);
    const item = (bg, border, label) => (
      <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155' }}>
        <span
          style={{
            width: 18,
            height: 14,
            borderRadius: 6,
            backgroundColor: bg,
            boxSizing: 'border-box',
            border: border || 'none',
            flexShrink: 0,
          }}
        />
        {label}
      </span>
    );
    return (
      <>
        <Button
          size="small"
          variant="outlined"
          color="default"
          aria-label="Справка по расписанию"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : 'false'}
          onClick={(e) => this.setState({ legendHelpAnchorEl: e.currentTarget })}
          className={classes.compactButton}
          style={{ marginLeft: 'auto' }}
        >
          <MdOutlineHelpOutline size={14} style={{ marginRight: 4 }} aria-hidden />
          Справка
        </Button>
        <Popover
          open={open}
          anchorEl={legendHelpAnchorEl}
          onClose={() => this.setState({ legendHelpAnchorEl: null })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          classes={{ paper: classes.legendHelpPaper }}
        >
          <div className={classes.legendHelpPanel}>
            <Typography variant="subtitle2" style={{ marginBottom: 10, fontWeight: 700 }}>
              Цвета слотов
            </Typography>
            <div className={classes.legendHelpSwatches}>
              {item(TIMETABLE_CHIP.free.bg, `1px solid ${TIMETABLE_GRID_LINE}`, 'Свободно')}
              {item(TIMETABLE_CHIP.unpaid.bg, 'none', 'Бронь (не оплачено)')}
              {item(TIMETABLE_CHIP.paid.bg, 'none', 'Оплачено')}
              {item(TIMETABLE_CHIP.admin.bg, 'none', 'Бронь администратором')}
              {item(TIMETABLE_CHIP.closed.bg, `1px solid ${TIMETABLE_GRID_LINE}`, 'Вне графика')}
              {item(TIMETABLE_CHIP.section.bg, 'none', 'Групповое (без группы — зелёный)')}
              {item(TIMETABLE_CHIP.personal.bg, 'none', 'Персональная')}
              {item(TIMETABLE_CHIP.past.bg, `1px solid ${TIMETABLE_GRID_LINE}`, 'Прошедшее')}
            </div>
            <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 10, marginBottom: 4 }}>
              Группы — свой цвет из палитры (по id группы):
            </Typography>
            <div className={classes.legendHelpSwatches} style={{ gap: 6 }}>
              {SECTION_GROUP_SLOT_PALETTE.map((bg, i) => (
                <span
                  key={bg}
                  title={`Палитра ${i + 1}`}
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 4,
                    backgroundColor: bg,
                    flexShrink: 0,
                  }}
                />
              ))}
            </div>
            <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 12, lineHeight: 1.45 }}>
              {TIMETABLE_CLIENT_HINT}{' '}
              Слоты секций и аренды склеиваются в один блок. Перенос — за середину; растянуть —
              за верх/низ или маркер справа снизу. Клик или выделение свободных слотов —
              аренда, группа или персональная. Прошедшие занятия открываются для просмотра
              и явки; новую аренду в прошлом создать нельзя. Период: стрелки в шапке или
              горизонтальный скролл / свайп по сетке (на узком экране — после края колонок).
            </Typography>
          </div>
        </Popover>
      </>
    );
  };

  renderSectionEventDialog = () => {
    const {
      sectionEventDialogOpen,
      sectionEventDialogData,
      sectionEventRescheduleDate,
      sectionEventRescheduleTime,
      sectionEventRescheduleEndDate,
      sectionEventRescheduleEndTime,
      sectionEventRescheduleSaving,
      sectionEventRescheduleError,
      occurrenceRoster,
      occurrenceRosterLoading,
      occurrenceRosterError,
      occurrenceRosterAddUserId,
      occurrenceRosterSaving,
    } = this.state;
    const { sportComplexId } = this.props;
    if (!sectionEventDialogOpen || !sectionEventDialogData) return null;

    const data = sectionEventDialogData;
    const startM = this.parseOccurrenceMoment(data.startAt);
    const endM = this.parseOccurrenceMoment(data.endAt);
    const timeRange =
      startM.isValid() && endM.isValid()
        ? `${startM.format('DD.MM.YYYY HH:mm')} – ${endM.format('HH:mm')}`
        : '—';
    const durationMin = this.getEventDurationMinutes(data);
    const previewStart = moment(
      `${sectionEventRescheduleDate || ''} ${sectionEventRescheduleTime || ''}`,
      'YYYY-MM-DD HH:mm',
      true,
    );
    const previewEnd = moment(
      `${sectionEventRescheduleEndDate || ''} ${sectionEventRescheduleEndTime || ''}`,
      'YYYY-MM-DD HH:mm',
      true,
    );
    const previewDuration =
      previewStart.isValid() && previewEnd.isValid() && previewEnd.isAfter(previewStart)
        ? previewEnd.diff(previewStart, 'minutes')
        : durationMin;
    const isPersonal = data.kind === 'personal_training';
    const title = isPersonal ? 'Персональная тренировка' : 'Занятие секции';
    const booked = data.bookedCount != null ? Number(data.bookedCount) : null;
    const maxP = data.maxParticipants != null ? Number(data.maxParticipants) : null;
    const seats =
      booked != null && !Number.isNaN(booked)
        ? (maxP != null && maxP > 0 ? `${booked} / ${maxP}` : `${booked}`)
        : null;
    const canReschedule = data.id != null && (isPersonal || data.sectionId != null);
    const sessionStarted = startM.isValid() && !moment().isBefore(startM);
    const busy = sectionEventRescheduleSaving || occurrenceRosterSaving;

    return (
      <Dialog
        open={sectionEventDialogOpen}
        onClose={this.closeSectionEventDialog}
        fullWidth
        maxWidth={isPersonal ? 'xs' : 'sm'}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            <strong>{data.label || '—'}</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Сейчас: {timeRange} ({durationMin} мин)
          </Typography>
          {data.sectionName && (
            <Typography variant="body2" color="textSecondary">
              Секция: {data.sectionName}
            </Typography>
          )}
          {data.sectionGroupName && !isPersonal && (
            <Typography variant="body2" color="textSecondary">
              Группа: {data.sectionGroupName}
            </Typography>
          )}
          {seats != null && (
            <Typography variant="body2" color="textSecondary">
              Записано мест: {seats}
            </Typography>
          )}
          {data.trainerId != null && (
            <Typography variant="body2" color="textSecondary">
              Тренер: {this.trainerLabelById(data.trainerId) || `#${data.trainerId}`}
            </Typography>
          )}
          {!isPersonal && (
            <div style={{ marginTop: 16 }}>
              <Typography variant="subtitle2" gutterBottom>
                Участники
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" paragraph>
                Пока не нажали «Пришёл» / «Не пришёл» — визит по пакету не спишется. Кто пришёл без записи — сначала в список.
              </Typography>
              {occurrenceRosterLoading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="textSecondary">Загрузка списка…</Typography>
                </div>
              )}
              {!occurrenceRosterLoading && (!occurrenceRoster || occurrenceRoster.length === 0) && (
                <Typography variant="body2" color="textSecondary" paragraph>
                  Пока никто не записан
                </Typography>
              )}
              {!occurrenceRosterLoading && (occurrenceRoster || []).map((row) => {
                const bookingId = row.id != null ? row.id : row.Id;
                const status = normalizeAttendanceStatus(row);
                const statusText = attendanceStatusLabel(status, { sessionStarted });
                return (
                  <div
                    key={bookingId}
                    style={{
                      borderTop: '1px solid #eee',
                      padding: '8px 0',
                    }}
                  >
                    <Typography variant="body2">{rosterPersonLabel(row)}</Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {statusText}
                    </Typography>
                    {sessionStarted ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                        <Button
                          size="small"
                          variant={status === ATTENDANCE_ATTENDED ? 'contained' : 'outlined'}
                          color="primary"
                          disabled={busy}
                          onClick={() => this.markOccurrenceAttendance(bookingId, 'attended')}
                        >
                          Пришёл
                        </Button>
                        <Button
                          size="small"
                          variant={status === ATTENDANCE_NO_SHOW ? 'contained' : 'outlined'}
                          disabled={busy}
                          onClick={() => this.markOccurrenceAttendance(bookingId, 'no_show')}
                        >
                          Не пришёл
                        </Button>
                        <Button
                          size="small"
                          disabled={busy || status === ATTENDANCE_UNKNOWN}
                          onClick={() => this.markOccurrenceAttendance(bookingId, 'unknown')}
                        >
                          Сбросить
                        </Button>
                      </div>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        Отметка явки доступна с начала занятия
                      </Typography>
                    )}
                  </div>
                );
              })}
              <div style={{ marginTop: 8 }}>
                <ComplexClientPicker
                  selectId="occurrence-roster-client"
                  options={this.state.complexClientOptions || []}
                  value={occurrenceRosterAddUserId || ''}
                  disabled={busy || !!this.state.complexClientsLoading}
                  loading={!!this.state.complexClientsLoading}
                  preferPaidHint
                  helperText="Можно выбрать карточку без приложения. Для записи нужен действующий абонемент."
                  orgId={this.state.organizationId}
                  onCreated={() => this.loadComplexClientsForPicker(data.sectionId)}
                  onChange={(userId) =>
                    this.setState({ occurrenceRosterAddUserId: userId, occurrenceRosterError: '' })
                  }
                />
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={this.addOccurrenceRosterBooking}
                  disabled={busy}
                  style={{ marginTop: 4 }}
                >
                  Записать
                </Button>
              </div>
              {occurrenceRosterError && (
                <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
                  {occurrenceRosterError}
                </Typography>
              )}
            </div>
          )}
          {canReschedule && (
            <div style={{ marginTop: 12 }}>
              <Typography variant="subtitle2" gutterBottom>
                Время занятия
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                На сетке: верхний слот меняет начало, нижний — конец, середина переносит целиком.
                Здесь можно задать точные начало и конец (длительность не обязана быть 60 мин).
              </Typography>
              <TextField
                margin="dense"
                label="Дата начала"
                type="date"
                value={sectionEventRescheduleDate || ''}
                onChange={(e) =>
                  this.setState({
                    sectionEventRescheduleDate: e.target.value,
                    sectionEventRescheduleError: '',
                  })
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={busy}
              />
              <TextField
                margin="dense"
                label="Время начала"
                type="time"
                value={sectionEventRescheduleTime || ''}
                onChange={(e) =>
                  this.setState({
                    sectionEventRescheduleTime: e.target.value,
                    sectionEventRescheduleError: '',
                  })
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={busy}
              />
              <TextField
                margin="dense"
                label="Дата конца"
                type="date"
                value={sectionEventRescheduleEndDate || ''}
                onChange={(e) =>
                  this.setState({
                    sectionEventRescheduleEndDate: e.target.value,
                    sectionEventRescheduleError: '',
                  })
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={busy}
              />
              <TextField
                margin="dense"
                label="Время конца"
                type="time"
                value={sectionEventRescheduleEndTime || ''}
                onChange={(e) =>
                  this.setState({
                    sectionEventRescheduleEndTime: e.target.value,
                    sectionEventRescheduleError: '',
                  })
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={busy}
                helperText={`Длительность: ${previewDuration} мин`}
              />
            </div>
          )}
          {sectionEventRescheduleError && (
            <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
              {sectionEventRescheduleError}
            </Typography>
          )}
          <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
            Аренда на этот интервал недоступна — слот занят секцией или персональной тренировкой.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeSectionEventDialog} disabled={busy}>
            Закрыть
          </Button>
          {canReschedule && (
            <Button
              color="primary"
              onClick={this.saveSectionEventReschedule}
              disabled={busy}
            >
              {sectionEventRescheduleSaving ? 'Сохранение…' : 'Сохранить время'}
            </Button>
          )}
          {sportComplexId != null && data.sectionId != null && (
            <Button color="primary" variant="contained" onClick={this.goToSectionFromEvent} disabled={busy}>
              К секции
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  renderCreatePersonalDialog = () => {
    const {
      createPersonalDialogOpen,
      createPersonalForm,
      createPersonalSaving,
      createPersonalError,
      trainersError,
    } = this.state;
    if (!createPersonalDialogOpen || !createPersonalForm) return null;

    const sections = this.personalSectionsForCreate();
    const trainers = this.trainerSelectOptions();
    const currentTrainer = createPersonalForm.trainerId
      ? String(createPersonalForm.trainerId)
      : '';
    const hasCurrentTrainer = trainers.some((u) => String(u.id) === currentTrainer);

    const setForm = (patch) => {
      this.setState((prev) => ({
        createPersonalForm: { ...prev.createPersonalForm, ...patch },
        createPersonalError: '',
      }));
    };

    return (
      <Dialog
        open={createPersonalDialogOpen}
        onClose={this.closeCreatePersonalDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Новая персональная тренировка</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            Слот с сетки: начало и конец можно поправить. Площадка подставится автоматически.
          </Typography>
          <FormControl fullWidth margin="dense">
            <InputLabel>Секция</InputLabel>
            <Select
              value={createPersonalForm.sectionId || ''}
              onChange={(e) => {
                setForm({ sectionId: e.target.value });
                this.loadComplexClientsForPicker(e.target.value);
              }}
              disabled={createPersonalSaving}
            >
              {sections.map((s) => {
                const id = s.id != null ? s.id : s.Id;
                return (
                  <MenuItem key={id} value={String(id)}>
                    {s.name || s.Name || `Секция #${id}`}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <ComplexClientPicker
            selectId="timetable-personal-client"
            options={this.state.complexClientOptions || []}
            value={createPersonalForm.userId || ''}
            disabled={createPersonalSaving || !!this.state.complexClientsLoading}
            loading={!!this.state.complexClientsLoading}
            preferPaidHint
            helperText="Можно выбрать карточку без приложения — аккаунт создастся по телефону при сохранении."
            orgId={this.state.organizationId}
            onCreated={() => this.loadComplexClientsForPicker(createPersonalForm.sectionId || null)}
            onChange={(userId) => setForm({ userId: userId })}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel shrink>Тренер</InputLabel>
            <Select
              value={currentTrainer}
              onChange={(e) => setForm({ trainerId: e.target.value })}
              disabled={createPersonalSaving}
              displayEmpty
            >
              <MenuItem value="">
                <em>Не задан</em>
              </MenuItem>
              {!hasCurrentTrainer && currentTrainer !== '' && (
                <MenuItem value={currentTrainer}>
                  {`${this.trainerLabelById(currentTrainer) || `#${currentTrainer}`} (не в списке сотрудников)`}
                </MenuItem>
              )}
              {trainers.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>
                  {u.label || formatUserLabel(u)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {trainersError && (
            <Typography variant="caption" color="error" display="block">
              {trainersError}
            </Typography>
          )}
          {!trainersError && trainers.length === 0 && (
            <Typography variant="caption" color="textSecondary" display="block">
              У клуба пока нет сотрудников. Добавьте их в разделе «Сотрудники».
            </Typography>
          )}
          {trainers.length > 0 && (
            <Typography variant="caption" color="textSecondary" display="block">
              Только сотрудники организации этого клуба.
            </Typography>
          )}
          <TextField
            margin="dense"
            label="Дата начала"
            type="date"
            value={createPersonalForm.startDate || ''}
            onChange={(e) => setForm({ startDate: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={createPersonalSaving}
          />
          <TextField
            margin="dense"
            label="Время начала"
            type="time"
            value={createPersonalForm.startTime || ''}
            onChange={(e) => setForm({ startTime: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={createPersonalSaving}
          />
          <TextField
            margin="dense"
            label="Дата конца"
            type="date"
            value={createPersonalForm.endDate || ''}
            onChange={(e) => setForm({ endDate: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={createPersonalSaving}
          />
          <TextField
            margin="dense"
            label="Время конца"
            type="time"
            value={createPersonalForm.endTime || ''}
            onChange={(e) => setForm({ endTime: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={createPersonalSaving}
          />
          {createPersonalError && (
            <Typography variant="body2" color="error" style={{ marginTop: 8, whiteSpace: 'pre-line' }}>
              {createPersonalError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeCreatePersonalDialog} disabled={createPersonalSaving}>
            Отмена
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={this.saveCreatePersonalFromSlot}
            disabled={createPersonalSaving}
          >
            {createPersonalSaving ? 'Создание…' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  renderSlotActionChooser = () => {
    const { slotActionChooserOpen, slotActionContext } = this.state;
    if (!slotActionChooserOpen || !slotActionContext) return null;
    const startLocal = this.intervalToLocalMoment(slotActionContext.day, slotActionContext.interval);
    const endExclusive = slotActionContext.endIntervalExclusive;
    const endLocal = endExclusive != null
      ? this.intervalToLocalMoment(slotActionContext.day, endExclusive)
      : null;
    const timeLabel = startLocal.isValid()
      ? (endLocal && endLocal.isValid() && endLocal.isAfter(startLocal)
        ? `${startLocal.format('DD.MM HH:mm')}–${endLocal.format('HH:mm')}`
        : startLocal.format('DD.MM HH:mm'))
      : '—';
    const hasSections = (this.state.sectionsCatalog || []).length > 0;
    const hasPersonalSections = (this.personalSectionsForCreate() || []).length > 0;
    const btnStyle = { justifyContent: 'flex-start', marginBottom: 8, textTransform: 'none' };
    const isRange = !!(slotActionContext.selectedRange
      && slotActionContext.selectedRange.endInterval != null
      && Number(slotActionContext.selectedRange.endInterval) > Number(slotActionContext.interval) + 1);

    return (
      <Dialog open={slotActionChooserOpen} onClose={this.closeSlotActionChooser} fullWidth maxWidth="xs">
        <DialogTitle>Что создать на {timeLabel}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            {isRange
              ? 'Выделен диапазон слотов. Выберите тип занятости — для группы и персоналки подставятся начало и конец выделения.'
              : 'Выберите тип занятости для этого слота.'}
          </Typography>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            style={btnStyle}
            onClick={this.chooseSlotActionRent}
          >
            Аренда площадки
          </Button>
          <Button
            fullWidth
            variant="outlined"
            style={btnStyle}
            disabled={!hasSections}
            onClick={this.chooseSlotActionGroup}
          >
            Групповое занятие секции
          </Button>
          <Button
            fullWidth
            variant="outlined"
            style={btnStyle}
            disabled={!hasPersonalSections}
            onClick={this.chooseSlotActionPersonal}
          >
            Персональная тренировка
          </Button>
          <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
            Регулярное расписание групп — во вкладке «Расписание» секции (шаблон недели).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeSlotActionChooser}>Отмена</Button>
        </DialogActions>
      </Dialog>
    );
  };

  renderCreateGroupDialog = () => {
    const {
      createGroupDialogOpen,
      createGroupForm,
      createGroupSaving,
      createGroupError,
      createGroupGroups,
      createGroupGroupsLoading,
      trainersError,
      sectionsCatalog,
    } = this.state;
    if (!createGroupDialogOpen || !createGroupForm) return null;

    const trainers = this.trainerSelectOptions();
    const currentTrainer = createGroupForm.trainerId ? String(createGroupForm.trainerId) : '';
    const hasCurrentTrainer = trainers.some((u) => String(u.id) === currentTrainer);

    const setForm = (patch) => {
      this.setState((prev) => ({
        createGroupForm: { ...prev.createGroupForm, ...patch },
        createGroupError: '',
      }));
    };

    return (
      <Dialog
        open={createGroupDialogOpen}
        onClose={this.closeCreateGroupDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Разовое групповое занятие</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            Создаёт одно занятие для выбранной группы на этой площадке (не меняет шаблон недели).
          </Typography>
          <FormControl fullWidth margin="dense">
            <InputLabel>Секция</InputLabel>
            <Select
              value={createGroupForm.sectionId || ''}
              onChange={(e) => {
                const sectionId = e.target.value;
                setForm({ sectionId, groupId: '', trainerId: '' });
                this.loadGroupsForCreateDialog(sectionId);
              }}
              disabled={createGroupSaving}
            >
              {(sectionsCatalog || []).map((s) => {
                const id = s.id != null ? s.id : s.Id;
                return (
                  <MenuItem key={id} value={String(id)}>
                    {s.name || s.Name || `Секция #${id}`}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Группа</InputLabel>
            <Select
              value={createGroupForm.groupId || ''}
              onChange={(e) => {
                const groupId = e.target.value;
                const selected = (createGroupGroups || []).find(
                  (g) => String(g.id != null ? g.id : g.Id) === String(groupId),
                );
                const trainerFromGroup = selected && (selected.trainerId != null ? selected.trainerId : selected.TrainerId);
                setForm({
                  groupId,
                  trainerId: trainerFromGroup != null && trainerFromGroup !== ''
                    ? String(trainerFromGroup)
                    : '',
                });
              }}
              disabled={createGroupSaving || createGroupGroupsLoading || !createGroupForm.sectionId}
            >
              {(createGroupGroups || []).map((g) => {
                const id = g.id != null ? g.id : g.Id;
                return (
                  <MenuItem key={id} value={String(id)}>
                    {g.name || g.Name || `Группа #${id}`}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {createGroupGroupsLoading && (
            <Typography variant="caption" color="textSecondary">Загрузка групп…</Typography>
          )}
          <FormControl fullWidth margin="dense">
            <InputLabel shrink>Тренер</InputLabel>
            <Select
              value={currentTrainer}
              onChange={(e) => setForm({ trainerId: e.target.value })}
              disabled={createGroupSaving}
              displayEmpty
            >
              <MenuItem value="">
                <em>Из группы / не задан</em>
              </MenuItem>
              {!hasCurrentTrainer && currentTrainer !== '' && (
                <MenuItem value={currentTrainer}>
                  {`${this.trainerLabelById(currentTrainer) || `#${currentTrainer}`} (не в списке сотрудников)`}
                </MenuItem>
              )}
              {trainers.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>
                  {u.label || formatUserLabel(u)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {trainersError && (
            <Typography variant="caption" color="error" display="block">
              {trainersError}
            </Typography>
          )}
          {!trainersError && trainers.length === 0 && (
            <Typography variant="caption" color="textSecondary" display="block">
              У клуба пока нет сотрудников. Добавьте их в разделе «Сотрудники».
            </Typography>
          )}
          {trainers.length > 0 && (
            <Typography variant="caption" color="textSecondary" display="block">
              Только сотрудники организации этого клуба.
            </Typography>
          )}
          <TextField
            margin="dense"
            label="Дата начала"
            type="date"
            value={createGroupForm.startDate || ''}
            onChange={(e) => setForm({ startDate: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={createGroupSaving}
          />
          <TextField
            margin="dense"
            label="Время начала"
            type="time"
            value={createGroupForm.startTime || ''}
            onChange={(e) => setForm({ startTime: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={createGroupSaving}
          />
          <TextField
            margin="dense"
            label="Дата конца"
            type="date"
            value={createGroupForm.endDate || ''}
            onChange={(e) => setForm({ endDate: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={createGroupSaving}
          />
          <TextField
            margin="dense"
            label="Время конца"
            type="time"
            value={createGroupForm.endTime || ''}
            onChange={(e) => setForm({ endTime: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
            disabled={createGroupSaving}
          />
          {createGroupError && (
            <Typography variant="body2" color="error" style={{ marginTop: 8, whiteSpace: 'pre-line' }}>
              {createGroupError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={this.closeCreateGroupDialog} disabled={createGroupSaving}>
            Отмена
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={this.saveCreateGroupFromSlot}
            disabled={createGroupSaving}
          >
            {createGroupSaving ? 'Создание…' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Недельная или дневная сетка: столбцы — дни, строки — слоты по времени
  renderScheduleTable = () => {
    const { schedule, weekStart, timelineViewMode, selectedDate, sectionOccurrencesLoading } = this.state;
    const { classes } = this.props;

    const displayDays =
      timelineViewMode === 'week'
        ? times(7, (i) => weekStart.clone().add(i, 'days').format('YYYY-MM-DD'))
        : [selectedDate];

    const hasAnyData = displayDays.some((d) => schedule && schedule[d] && schedule[d].length > 0);
    const playgroundId = this.props.playgroundId;
    const minTableWidth = timelineViewMode === 'week' ? 840 : 400;

    if (!schedule || !hasAnyData) {
      return (
        <div>
          <div className={classes.sheetChrome}>
            {this.renderTimelinePeriodHeader()}
          </div>
          <div style={{ padding: 20, textAlign: 'center', maxWidth: 520, margin: '0 auto' }}>
            <Typography variant="body1" paragraph>
              Нет данных расписания для выбранного периода.
        </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Сетка строится из <strong>режима работы</strong> площадки. Если вы ведёте только секции
              (без аренды), укажите слоты с ценой <strong>0</strong> — календарь появится, занятия
              секций можно будет ставить поверх.
            </Typography>
            {playgroundId != null && (
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  if (this.props.history) {
                    this.props.history.push(`/playground/${playgroundId}`);
                  }
                }}
              >
                Настроить режим работы площадки
              </Button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        className={classes.sheetPane}
        onTouchStart={this.handleSheetPeriodTouchStart}
        onTouchMove={this.handleSheetPeriodTouchMove}
        onTouchEnd={this.handleSheetPeriodTouchEnd}
      >
        <div className={classes.sheetChrome}>
          {this.renderTimelinePeriodHeader()}
          {sectionOccurrencesLoading && (
            <Typography variant="caption" className={classes.sheetChromeHint}>
              Загрузка оверлея секций…
            </Typography>
          )}
        </div>
        <div className={classes.sheetWrap} ref={this.setSheetWrapRef}>
          <table className={classes.sheetTable} style={{ minWidth: minTableWidth }}>
            <colgroup>
              <col className={classes.sheetTimeCol} />
              {displayDays.map((dateStr) => (
                <col key={dateStr} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th className={classNames(classes.sheetHeadCell, classes.sheetHeadTime)}>
                  Время
                </th>
                {displayDays.map((dateStr) => {
                  const d = moment(dateStr, 'YYYY-MM-DD');
                  const isTodayCol = dateStr === today;
                  return (
                    <th
                      key={dateStr}
                      className={classNames(classes.sheetHeadCell, isTodayCol && classes.sheetHeadToday)}
                    >
                      <div className={classes.sheetHeadWeekday}>{d.format('dd')}</div>
                      <div className={classes.sheetHeadDate}>{d.format('D MMM')}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {(() => {
                const spansByDay = {};
                displayDays.forEach((dateStr) => {
                  spansByDay[dateStr] = this.getDayRowspans(dateStr, schedule[dateStr]);
                });
                return times(48, (interval) => {
                  const isHourStart = interval % 2 === 0;
                  const slotTime = intervalSlotTimeRange(interval);
                  const isLastSlot = interval === 47;
                return (
                    <tr key={interval} className={isHourStart ? classes.sheetHourRow : undefined}>
                      <td
                        className={classNames(classes.sheetTimeCell, !isHourStart && classes.sheetTimeHalf)}
                        title={slotTime.title}
                      >
                        <span
                          className={classNames(
                            classes.sheetTimeTick,
                            !isHourStart && classes.sheetTimeTickHalf,
                            interval === 0 && classes.sheetTimeTickFirst,
                          )}
                        >
                          {slotTime.start}
                        </span>
                        {isLastSlot ? (
                          <span className={classes.sheetTimeTickLast}>00:00</span>
                        ) : null}
                    </td>
                    {displayDays.map((dateStr) => {
                      const hourArray = schedule[dateStr];
                        const span = (spansByDay[dateStr] && spansByDay[dateStr][interval])
                          || { skip: false, rowspan: 1, mergeKey: null };
                        if (span.skip) return null;
                      return (
                          <td
                            key={dateStr}
                            className={classNames(
                              classes.sheetSlotCell,
                              span.rowspan > 1 && classes.sheetSlotCellMerged,
                            )}
                            rowSpan={span.rowspan > 1 ? span.rowspan : undefined}
                            data-timetable-cell="true"
                            data-day={dateStr}
                            data-interval={interval}
                            data-rowspan={span.rowspan || 1}
                          >
                            {this.renderOneIntervalRow(dateStr, interval, hourArray, classes, span)}
                        </td>
                      );
                    })}
                  </tr>
                );
                });
              })()}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  render() {
    const {
      classes, currentWorkingHoursTypes, workingHoursTypes, sport, workingHoursSports,
    } = this.props;
    const {
      loading, schedule, intervalData,
      choosenDay,
      chosenPart,
      showDialog,
      selectedDate, // eslint-disable-line no-unused-vars
    } = this.state;
    // Проверяем, есть ли данные для текущего типа расписания
    // Для нового типа расписания может не быть ни расписания, ни видов спорта
    const hasSchedule = schedule && !isEmpty(schedule);
    const hasSports = workingHoursSports[currentWorkingHoursTypes] && 
                      workingHoursSports[currentWorkingHoursTypes].length > 0;
    
    // Если нет расписания или нет видов спорта для текущего типа, показываем сообщение
    if (!loading && (!hasSchedule || !hasSports)) {
      const currentTypeLabel = currentWorkingHoursTypes === 'full' ? 'целиком' : 
                               currentWorkingHoursTypes === 'half' ? '1/2' : '1/3';
      return (
        <Paper className={classes.paperImages} elevation={2}>
          <Typography type="body1">
            Для формата "{currentTypeLabel}" необходимо сначала настроить режим работы и выбрать виды спорта.
            Прокрутите вниз до раздела "Режим работы" для настройки.
          </Typography>
        </Paper>
      );
    }

    return (
      <div>
        <AppBar className={classes.paper} position="static" color="default">
          <Toolbar className={classes.toolbar} disableGutters={false}>
            <Tabs
              value={currentWorkingHoursTypes}
              onChange={(event, value) => {
                this.props.changeCurrentWorkingHoursType(value);
                this.loadWeekData(this.state.weekStart, value);
                this.loadMonthData(this.state.currentMonth, value);
              }}
              className={classes.tabs}
            >
              {workingHoursTypes.map((item) => {
                switch (item) {
                case workingHoursTypeEnum.full:
                  return <Tab label="Целиком" key={item} value={item} />;
                case workingHoursTypeEnum.half:
                  return <Tab label="1/2" key={item} value={item} />;
                case workingHoursTypeEnum.third:
                  return <Tab label="1/3" key={item} value={item} />;
                default:
                  return '';
                }
              })}
            </Tabs>
            <div className={classes.saveButtonContainer}>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={this.handleDownloadTemplate}
                className={classes.toolbarButton}
              >
                <MdOutlineAssignment className={classes.buttonIcon} size={16} aria-hidden /> Шаблон
              </Button>
      <input
        accept=".csv"
        style={{ display: 'none' }}
        id="import-excel-button"
        type="file"
        onChange={this.handleImportExcel}
      />
              <label htmlFor="import-excel-button" className={classes.toolbarButtonLabel}>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  component="span"
                  className={classes.toolbarButton}
                >
                  <MdOutlineFileDownload className={classes.buttonIcon} size={16} aria-hidden /> Импорт
                </Button>
              </label>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={this.handleExportExcel}
                className={classes.toolbarButton}
              >
                <MdOutlineFileUpload className={classes.buttonIcon} size={16} aria-hidden /> Экспорт
              </Button>
              {workingHoursTypes.length < 3 ?
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  className={classes.toolbarButton}
                  onClick={this.props.addWorkingHoursType}
                >
                  Добавить
                </Button> : ''}
            </div>
          </Toolbar>
        </AppBar>
        <Paper className={classes.paperImages} elevation={2}>
          <div style={{ marginBottom: 25 }}>
            <Typography type="h5" style={{ marginBottom: 8 }}>Расписание</Typography>
            {(() => {
              // Получаем виды спорта для текущего типа расписания
              let availableSports = workingHoursSports[currentWorkingHoursTypes] || [];
              
              // Преобразуем числовые коды в строковые ключи, если нужно
              if (availableSports.length > 0 && typeof availableSports[0] === 'number') {
                availableSports = sportFlagsToKeysArray(availableSports);
              }
              
              // Преобразуем в читаемый формат (названия через запятую)
              const sportsLabels = availableSports
                .filter(sportItem => {
                  return sportItem && 
                         typeof sportItem === 'string' && 
                         sportsEnum[sportItem];
                })
                .map(sportItem => {
                  const label = sportsEnum[sportItem];
                  return label || sportItem;
                });
              
              if (sportsLabels.length > 0) {
                return (
                  <Typography type="body2" style={{ color: '#666', fontStyle: 'italic', marginTop: 5 }}>
                    Виды спорта: {sportsLabels.join(', ')}
                  </Typography>
                );
              }
              return null;
            })()}
            <div className={classes.viewModeRow}>
              <Typography variant="body2" className={classes.viewModeLabel}>
                Вид:
              </Typography>
              <Button
                size="small"
                variant={this.state.timelineViewMode === 'week' ? 'contained' : 'outlined'}
                color="primary"
                className={classes.compactButton}
                onClick={() => this.setTimelineViewMode('week')}
              >
                Неделя
              </Button>
              <Button
                size="small"
                variant={this.state.timelineViewMode === 'day' ? 'contained' : 'outlined'}
                color="primary"
                className={classes.compactButton}
                onClick={() => this.setTimelineViewMode('day')}
              >
                День
              </Button>
              {this.renderTimelineLegend()}
            </div>
            {this.props.sportComplexId ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16, alignItems: 'flex-end' }}>
                <FormControl margin="dense" style={{ minWidth: 220 }}>
                  <InputLabel shrink htmlFor="timetable-section-filter">
                    Фильтр секции
                  </InputLabel>
                  <Select
                    id="timetable-section-filter"
                    value={this.state.sectionFilterId}
                    onChange={this.handleSectionFilterChange}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Все секции на площадке</em>
                    </MenuItem>
                    {this.state.sectionsCatalog.map((s) => (
                      <MenuItem key={s.id} value={String(s.id)}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl margin="dense" style={{ minWidth: 200 }} disabled={!this.state.sectionFilterId}>
                  <InputLabel shrink htmlFor="timetable-group-filter">
                    Группа
                  </InputLabel>
                  <Select
                    id="timetable-group-filter"
                    value={this.state.groupFilterId}
                    onChange={this.handleGroupFilterChange}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Все группы</em>
                    </MenuItem>
                    {this.state.groupsCatalog.map((g) => (
                      <MenuItem key={g.id} value={String(g.id)}>
                        {g.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {(this.state.sectionsCatalogError || this.state.groupsCatalogError) && (
                  <Typography variant="body2" color="error" component="div" style={{ flex: '1 1 100%' }}>
                    {this.state.sectionsCatalogError}
                    {this.state.sectionsCatalogError && this.state.groupsCatalogError ? ' ' : ''}
                    {this.state.groupsCatalogError}
                  </Typography>
                )}
              </div>
            ) : (
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                Привяжите площадку к комплексу, чтобы фильтровать секции на таймлайне.
              </Typography>
            )}
          </div>
          
          {showDialog && <BookingDialog
            intervalData={intervalData}
            show={showDialog}
            sport={sport}
            choosenDay={choosenDay}
            schedule={schedule}
            currentWorkingHoursTypes={currentWorkingHoursTypes}
            chosenPart={chosenPart}
            loading={loading}
            deleteBooking={this.deleteBooking}
            onCancel={() => {
              this.setState({ showDialog: false });
              this.selectedRange = null; // Сбрасываем выбранный диапазон
            }}
            onSave={this.bookInterval}
            workingHoursSports={workingHoursSports}
            selectedRange={this.selectedRange} // Передаем выбранный диапазон
            playgroundId={this.props.playgroundId}
            sportComplexId={this.props.sportComplexId}
            onRecurringCreated={() => {
              this.loadWeekData(this.state.weekStart, currentWorkingHoursTypes);
              this.loadMonthData(this.state.currentMonth, currentWorkingHoursTypes);
            }}
          />}
              <div style={{ position: 'relative', minHeight: 50 }}>
                {this.state.loading && <CircularProgress
                  size={42}
                  color="secondary"
                  style={{
                    top: '15%',
                    left: '50%',
                    zIndex: 1,
                    position: 'absolute',
                  }}
                />}
                {this.renderScheduleTable()}
              </div>
          <Snackbar
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            open={this.state.isSnackOpen}
            onClose={() => this.setState({ isSnackOpen: false })}
            SnackbarContentProps={{
              'aria-describedby': 'message-id',
            }}
            message={
              <span id="message-id" style={{ maxWidth: 480, whiteSpace: 'pre-line' }}>
                {this.state.snackMessage}
              </span>
            }
          />
          {this.renderSectionEventDialog()}
          {this.renderSlotActionChooser()}
          {this.renderCreatePersonalDialog()}
          {this.renderCreateGroupDialog()}

        </Paper>
      </div>
    );
  }
}

const styles = (theme) => ({
  paper: {
    overflowX: 'auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    width: '100%',
    maxWidth: 1400,
    alignItems: 'stretch',
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    backgroundColor: '#ffffff',
    transition: 'box-shadow 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    },
  },
  paperImages: {
    overflowX: 'auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    width: '100%',
    maxWidth: 1400,
    alignItems: 'stretch',
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    backgroundColor: '#ffffff',
    transition: 'box-shadow 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    },
  },
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
  },
  flex: {
    flex: 1,
    display: 'flex',
  },
  flexRow: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  toolbar: {
    backgroundColor: 'transparent',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
  },
  periodBar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    marginBottom: 0,
    minWidth: 0,
    '& > * + *': {
      marginLeft: 8,
    },
  },
  todayButton: {
    textTransform: 'none',
    borderRadius: 6,
    fontWeight: 600,
    flex: '0 0 auto',
    minHeight: 28,
    padding: '2px 10px',
    fontSize: '0.75rem',
    lineHeight: 1.25,
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  periodNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '1 1 auto',
    minWidth: 0,
  },
  periodArrow: {
    width: 32,
    height: 32,
    padding: 0,
    flex: '0 0 auto',
  },
  periodButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    maxWidth: '100%',
    margin: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    borderRadius: 8,
    padding: '6px 10px',
    color: 'inherit',
    font: 'inherit',
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.08)',
    },
    '&:focus': {
      outline: 'none',
      backgroundColor: 'rgba(25, 118, 210, 0.12)',
    },
  },
  periodLabel: {
    fontWeight: 700,
    fontSize: '1.125rem',
    letterSpacing: '-0.01em',
    lineHeight: 1.3,
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.95rem',
    },
  },
  periodCaret: {
    color: theme.palette.primary.main,
    fontSize: 11,
    lineHeight: 1,
    marginTop: 2,
    opacity: 0.85,
  },
  calendarPopoverPaper: {
    borderRadius: 12,
    marginTop: 6,
    boxShadow: '0 8px 28px rgba(0,0,0,0.16)',
    overflow: 'hidden',
  },
  legendHelpPaper: {
    borderRadius: 12,
    marginTop: 6,
    boxShadow: '0 8px 28px rgba(0,0,0,0.16)',
    overflow: 'hidden',
  },
  legendHelpPanel: {
    width: 320,
    maxWidth: '92vw',
    padding: theme.spacing(2),
  },
  legendHelpSwatches: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  calendarPanel: {
    width: 280,
    padding: theme.spacing(1.5, 1.75, 2),
  },
  calendarMonthRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  calendarMonthLabel: {
    fontWeight: 700,
    fontSize: '0.95rem',
    textTransform: 'capitalize',
  },
  calendarWeekdays: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    marginBottom: 4,
  },
  calendarWeekday: {
    textAlign: 'center',
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    padding: '4px 0',
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
  },
  calendarCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 36,
  },
  calendarDay: {
    position: 'relative',
    width: 32,
    height: 32,
    padding: 0,
    border: 'none',
    background: 'transparent',
    borderRadius: '50%',
    cursor: 'pointer',
    fontSize: 13,
    lineHeight: 1,
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.06)',
    },
  },
  calendarDaySelected: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    fontWeight: 700,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  calendarDayToday: {
    boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  calendarDayInWeek: {
    backgroundColor: 'rgba(14, 147, 95, 0.12)',
    borderRadius: 8,
  },
  calendarDayPast: {
    color: theme.palette.text.disabled,
  },
  calendarDot: {
    position: 'absolute',
    bottom: 3,
    left: '50%',
    width: 4,
    height: 4,
    marginLeft: -2,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  },
  calendarDotOnSelected: {
    backgroundColor: '#fff',
  },
  button: {
    borderRadius: 8,
    padding: theme.spacing(1.25, 3),
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  toolbar: {
    minHeight: 44,
    paddingTop: 4,
    paddingBottom: 4,
    flexWrap: 'wrap',
  },
  toolbarButton: {
    minHeight: 30,
    padding: '3px 10px',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: 6,
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    lineHeight: 1.25,
    '&:hover': {
      boxShadow: 'none',
    },
  },
  toolbarButtonLabel: {
    display: 'inline-flex',
    margin: 0,
  },
  buttonIcon: {
    marginRight: 4,
    flexShrink: 0,
  },
  viewModeRow: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    marginTop: 8,
    marginBottom: 12,
    minWidth: 0,
    '& > * + *': {
      marginLeft: 6,
    },
  },
  viewModeLabel: {
    marginRight: 2,
    color: '#64748B',
    fontSize: '0.8125rem',
    flexShrink: 0,
  },
  compactButton: {
    minHeight: 28,
    padding: '2px 10px',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: 6,
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    lineHeight: 1.25,
    '&:hover': {
      boxShadow: 'none',
    },
  },
  tabs: {
    minHeight: 36,
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.8125rem',
      minHeight: 36,
      minWidth: 64,
      padding: '4px 10px',
      '&.Mui-selected': {
        fontWeight: 600,
      },
    },
    '& .MuiTabs-indicator': {
      height: 2,
      borderRadius: '2px 2px 0 0',
    },
  },
  flexElement: {
    flex: 1,
    alignItems: 'center',
  },
  red: {
    backgroundColor: TIMETABLE_CHIP.paid.bg,
    color: TIMETABLE_CHIP.paid.fg,
  },
  yellow: {
    backgroundColor: TIMETABLE_CHIP.unpaid.bg,
    color: TIMETABLE_CHIP.unpaid.fg,
  },
  green: {
    backgroundColor: TIMETABLE_CHIP.free.bg,
    color: TIMETABLE_CHIP.free.fg,
  },
  grey: {
    backgroundColor: TIMETABLE_CHIP.closed.bg,
    color: TIMETABLE_CHIP.closed.fg,
  },
  darkgrey: {
    backgroundColor: TIMETABLE_CHIP.admin.bg,
    color: TIMETABLE_CHIP.admin.fg,
  },
  btnGroup: {
    position: 'relative',
    display: 'flex',
    width: '100%',
    margin: 0,
    marginRight: 8,
  },
  intervalRow: {
    display: 'flex',
    width: '100%',
    marginBottom: 8,
  },
  hourGroup: {
    display: 'flex',
    width: '100%',
    marginBottom: 8,
  },
  btnGroupText: {
    position: 'absolute',
    zIndex: 1,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 'normal',
  },
  btnPeriod: {
    position: 'relative',
    flex: '1 1 auto',
    minWidth: 0,
    margin: 0,
    padding: '0 4px',
    width: '100%',
    height: '100%',
    maxHeight: '100%',
    minHeight: 0,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 400,
    lineHeight: 1.15,
    '&&': {
      minHeight: 0,
      height: '100%',
      padding: '0 4px',
      boxShadow: 'none',
      lineHeight: 1.15,
    },
    '&:hover': {
      boxShadow: 'none',
      filter: 'brightness(0.96)',
      backgroundColor: 'unset',
    },
    '&.Mui-disabled': {
      color: 'inherit',
      opacity: 1,
      minHeight: 0,
    },
    '&:after': {
      content: "''",
      position: 'absolute',
      zIndex: 2,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
    '& .MuiButton-label': {
      width: '100%',
      height: '100%',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      textAlign: 'left',
      overflow: 'hidden',
    },
  },
  /* Одна вертикальная прокрутка у сетки; период и дни остаются в видимой панели. */
  sheetPane: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxHeight: 'calc(100vh - 260px)',
    minHeight: 'min(320px, calc(100vh - 260px))',
  },
  sheetChrome: {
    flex: '0 0 auto',
    marginBottom: theme.spacing(1),
  },
  sheetChromeHint: {
    display: 'block',
    marginTop: theme.spacing(0.5),
    color: ADMIN_PRIMARY,
  },
  sheetWrap: {
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'auto',
    width: '100%',
    border: `1px solid ${TIMETABLE_GRID_LINE}`,
    borderRadius: 12,
    backgroundColor: '#fff',
    WebkitOverflowScrolling: 'touch',
    overscrollBehaviorX: 'contain',
    touchAction: 'pan-x pan-y',
  },
  sheetTable: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    tableLayout: 'fixed',
    fontSize: 12,
  },
  sheetTimeCol: {
    width: 52,
  },
  sheetHeadCell: {
    position: 'sticky',
    top: 0,
    zIndex: 6,
    padding: '8px 4px',
    textAlign: 'center',
    fontWeight: 700,
    fontSize: 12,
    color: '#1E293B',
    backgroundColor: '#F8FAFC',
    borderRight: `1px solid ${TIMETABLE_GRID_LINE}`,
    borderBottom: `1px solid ${TIMETABLE_GRID_LINE}`,
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  sheetHeadTime: {
    left: 0,
    zIndex: 7,
    textAlign: 'left',
    paddingLeft: 8,
    width: 52,
  },
  sheetHeadToday: {
    backgroundColor: 'rgba(14, 147, 95, 0.12)',
    color: ADMIN_PRIMARY,
  },
  sheetHeadWeekday: {
    textTransform: 'capitalize',
    fontSize: 11,
    fontWeight: 700,
  },
  sheetHeadDate: {
    fontSize: 11,
    fontWeight: 400,
    color: '#64748B',
    marginTop: 1,
  },
  sheetHourRow: {},
  sheetTimeCell: {
    position: 'sticky',
    left: 0,
    zIndex: 5,
    width: 52,
    padding: 0,
    textAlign: 'right',
    fontSize: 11,
    fontVariantNumeric: 'tabular-nums',
    color: '#64748B',
    backgroundColor: '#F8FAFC',
    borderRight: `1px solid ${TIMETABLE_GRID_LINE}`,
    borderBottom: `1px solid ${TIMETABLE_GRID_LINE}`,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    height: 30,
    maxHeight: 30,
    overflow: 'visible',
  },
  sheetTimeTick: {
    position: 'absolute',
    right: 6,
    top: 0,
    transform: 'translateY(-50%)',
    lineHeight: '12px',
    fontSize: 11,
    fontWeight: 700,
    color: '#334155',
    backgroundColor: '#F8FAFC',
    padding: '0 0 0 4px',
  },
  sheetTimeTickHalf: {
    fontSize: 10,
    fontWeight: 400,
    color: '#94A3B8',
  },
  sheetTimeTickFirst: {
    transform: 'none',
    top: 1,
  },
  sheetTimeTickLast: {
    position: 'absolute',
    right: 6,
    bottom: 0,
    transform: 'translateY(50%)',
    lineHeight: '12px',
    fontSize: 10,
    fontWeight: 400,
    color: '#94A3B8',
    backgroundColor: '#F8FAFC',
    padding: '0 0 0 4px',
  },
  sheetTimeHalf: {},
  sheetSlotCell: {
    position: 'relative',
    padding: 0,
    height: 30,
    maxHeight: 30,
    border: 'none',
    borderRight: `1px solid ${TIMETABLE_GRID_LINE}`,
    borderBottom: `1px solid ${TIMETABLE_GRID_LINE}`,
    verticalAlign: 'top',
    overflow: 'hidden',
    minWidth: 0,
    boxSizing: 'border-box',
    backgroundColor: '#fff',
  },
  sheetSlotCellMerged: {
    height: 'auto',
    maxHeight: 'none',
  },
  sheetSlotRow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    width: 'auto',
    height: 'auto',
    minHeight: 0,
    minWidth: 0,
    '& > *:not(:first-child)': {
      borderLeft: `1px solid ${TIMETABLE_GRID_LINE}`,
    },
  },
  sheetBlock: {
    position: 'relative',
    display: 'flex',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    height: '100%',
    overflow: 'hidden',
  },
  sheetBlockWrap: {
    position: 'relative',
    display: 'flex',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    height: '100%',
  },
  sheetResizeVacate: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(14, 147, 95, 0.16)',
    boxShadow: `inset 0 0 0 1px dashed ${ADMIN_PRIMARY}`,
    pointerEvents: 'none',
    zIndex: 0,
  },
  sheetResizeEdge: {
    position: 'absolute',
    left: 0,
    right: 10,
    height: 6,
    zIndex: 1,
    cursor: 'ns-resize',
  },
  sheetResizeEdgeTop: {
    top: 0,
  },
  sheetResizeEdgeBottom: {
    bottom: 0,
  },
  sheetFillHandle: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    width: 7,
    height: 7,
    backgroundColor: ADMIN_PRIMARY,
    border: '1px solid #fff',
    borderRadius: 2,
    zIndex: 1,
    cursor: 'ns-resize',
    boxSizing: 'border-box',
  },
  sheetSlotFill: {
    display: 'flex',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    height: '100%',
  },
  addButton: {
    marginRight: 20,
  },
  saveButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginLeft: 'auto',
    marginRight: 4,
    '& > * + *': {
      marginLeft: 6,
    },
  },
  tooltip: {
    display: 'flex',
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    height: '100%',
  },
});

const Styled = withStyles(styles)(Timetable);
export default withRouter(Styled);
